// VastuPersonsGateway.jsx — Shared person entry → Numero Vastu (Lo Shu + Kua) or Astro Vastu (D1 Chart)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Download, Save, Plus, Trash2, Hash, ChevronLeft, Star, Sparkles, MousePointerClick } from 'lucide-react';
import { LO_SHU_DATA, LO_SHU_GRID } from './NumeroVastuDefaultData';
import { loShuText, localDigits, nvLabel } from './NumeroVastuI18n';
import { buildKuaHtml, isDefaultKuaHtml, calcKuaNumber, calcMoolank, calcBhagyank, calcNumerologyMatch, getLuckyColors, getUnluckyColors, KUA_DATA, kuaDir, kuaElement, kuaGroup, kuaPersonality, kuaLifeArea, kuaColors, kuaLabel } from './KuaNumberData';
import { computeD1Chart, getVimshottariDasha, getAshtakavarga, getKpChart } from './AstroApiService';
import PlaceAutocomplete from './PlaceAutocomplete';
import { toPlaceGeo } from './PlaceSearchService';
import { buildAstroAnalysisHtml, isDefaultAstroHtml } from './AstroPlanetHouseMeanings';
import { NorthIndianChartSVG, SouthIndianChartSVG, drawNorthIndianChartPdf, drawSouthIndianChartPdf } from './NorthIndianChart';
import { VEDIC_VASTU_DIRECTIONS, LAAL_KITAAB_DIRECTIONS, computeAstroVastuDirections } from './VastuChartVariants';
import { computeNavataraChakra, buildActivityMapping, getNakshatra, getNakshatraDeity, getNakshatraBestDirection, GRAHA_PLANT_REMEDY, HIT_PLANETS, HIT_DISPLAY_HOUSES, generatePlanetPlanetGrid, generatePlanetHouseGrid, getZodiacFromLongitude, deityLabel, directionLabel, plantLabel, navataraLabel, activityLabel, hitNameLabel, compassCodeLabel, laalKitaabLabel, rashiLabel, planetLabel, kundaliTextLabel, ascLabel } from './KundaliReportsData';
import jsPDF from 'jspdf';
import useIsMobile from '../../../hooks/useIsMobile';
import { useLeaveConfirm } from './Configure/components/ConfirmLeaveDialog';

// jsPDF's built-in fonts have no Indic glyphs and no shaping engine
// (matras/conjuncts render out of order), so any line containing an
// Indic-script character is rasterised on an offscreen canvas — using the
// browser's own text shaping — and embedded as an image. Pure-Latin text
// still goes through doc.text() as before (crisp vector, no size cost).
// BUG FIX: only matched Devanagari (Hindi/Marathi) — Gujarati/Bengali/Tamil/
// Kannada/Telugu text failed this test and fell through to jsPDF's
// Latin-only font, printing as garbled symbols in the Kua/Numero/Astro
// standalone PDF downloads (same fix applied to the shared renderer in
// Configure/index.js and Space/Entrance Analysis's own PDF exports).
const DEVANAGARI_RE = /[ऀ-ॿ઀-૿ঀ-৿஀-௿ఀ-౿ಀ-೿]/;
const PX_PER_MM = 96 / 25.4;
const hindiMeasureCtx = document.createElement('canvas').getContext('2d');
const hindiFontCss = (fontSizePt, fontStyle) => {
  const isBold   = fontStyle === 'bold' || fontStyle === 'bolditalic';
  const isItalic = fontStyle === 'italic' || fontStyle === 'bolditalic';
  return `${isItalic ? 'italic ' : ''}${isBold ? '700' : '400'} ${(fontSizePt * 96 / 72).toFixed(2)}px "Noto Sans", "Noto Sans Devanagari", "Noto Sans Gujarati", "Noto Sans Bengali", "Noto Sans Tamil", "Noto Sans Kannada", "Noto Sans Telugu", "Nirmala UI", Arial, sans-serif`;
};
const createPdfTextHelpers = (doc) => {
  const wrapMixedText = (text, fontSizePt, fontStyle, maxWidthMM) => {
    hindiMeasureCtx.font = hindiFontCss(fontSizePt, fontStyle);
    const maxWidthPx = maxWidthMM * PX_PER_MM;
    const words = text.split(' ');
    const lines = [];
    let cur = '';
    words.forEach(word => {
      const test = cur ? cur + ' ' + word : word;
      if (cur && hindiMeasureCtx.measureText(test).width > maxWidthPx) { lines.push(cur); cur = word; } else { cur = test; }
    });
    if (cur) lines.push(cur);
    return lines;
  };
  const wrapAny = (text, fontSizePt, fontStyle, maxWidthMM) => {
    if (DEVANAGARI_RE.test(text)) return wrapMixedText(text, fontSizePt, fontStyle, maxWidthMM);
    doc.setFont('helvetica', fontStyle || 'normal');
    doc.setFontSize(fontSizePt);
    return doc.splitTextToSize(text, maxWidthMM);
  };
  const drawLineSmart = (line, x, yBaseline, fontSizePt, fontStyle, rgb, align) => {
    if (!DEVANAGARI_RE.test(line)) {
      doc.setFont('helvetica', fontStyle || 'normal');
      doc.setFontSize(fontSizePt);
      doc.setTextColor(...rgb);
      doc.text(line, x, yBaseline, align === 'center' ? { align: 'center' } : undefined);
      return;
    }
    const fontCss = hindiFontCss(fontSizePt, fontStyle);
    hindiMeasureCtx.font = fontCss;
    const scale = 3;
    const widthPx  = Math.ceil(hindiMeasureCtx.measureText(line).width) + 4;
    const heightPx = Math.ceil(fontSizePt * 96 / 72 * 1.4);
    const canvas = document.createElement('canvas');
    canvas.width = widthPx * scale;
    canvas.height = heightPx * scale;
    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);
    ctx.font = fontCss;
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = `rgb(${rgb.join(',')})`;
    ctx.fillText(line, 2, heightPx * 0.75);
    const widthMM  = widthPx  / PX_PER_MM;
    const heightMM = heightPx / PX_PER_MM;
    if (align === 'center') x -= widthMM / 2;
    doc.addImage(canvas.toDataURL('image/png'), 'PNG', x, yBaseline - heightMM * 0.75, widthMM, heightMM, undefined, 'FAST');
  };
  return { wrapAny, drawLineSmart };
};

const PERSONS_KEY = 'vastu_persons_data';
const NUMERO_KEY  = 'numero_vastu_data';   // kept for backward compat with Configure PDF generation
const KUA_KEY     = 'numero_vastu_kua_data';
const ASTRO_KEY   = 'astro_vastu_data';
// Which kundli styles the report shows: { north: bool, south: bool } — saved per project,
// read again by the combined PDF export in Configure/index.js (same key pattern).
const ASTRO_CHART_STYLE_KEY = 'astro_vastu_chart_styles';

// Bump this whenever buildKuaHtml()'s template/content changes — invalidates
// old saved Kua richHtml so corrected content actually reaches users who had
// already saved before the fix, instead of the stale cached copy winning
// forever (see enterKuaMode).
// v2 → v3: Kua template is now multilingual (buildKuaHtml(person, lang)).
const KUA_HTML_VERSION = 3;

// ── Astro Vastu constants ─────────────────────────────────────────────────────
export const RASHIS = [
  {num:1,  name:'Mesh',      en:'Aries'},
  {num:2,  name:'Vrishabh',  en:'Taurus'},
  {num:3,  name:'Mithun',    en:'Gemini'},
  {num:4,  name:'Kark',      en:'Cancer'},
  {num:5,  name:'Simha',     en:'Leo'},
  {num:6,  name:'Kanya',     en:'Virgo'},
  {num:7,  name:'Tula',      en:'Libra'},
  {num:8,  name:'Vrishchik', en:'Scorpio'},
  {num:9,  name:'Dhanu',     en:'Sagittarius'},
  {num:10, name:'Makar',     en:'Capricorn'},
  {num:11, name:'Kumbh',     en:'Aquarius'},
  {num:12, name:'Meen',      en:'Pisces'},
];

const GRAHAS = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'];

export const HOUSES = [
  {num:1,  name:'Lagna',   desc:'Self · Body · Personality'},
  {num:2,  name:'Dhan',    desc:'Wealth · Family · Speech'},
  {num:3,  name:'Sahaj',   desc:'Siblings · Courage · Communication'},
  {num:4,  name:'Matri',   desc:'Mother · Home · Comfort'},
  {num:5,  name:'Santan',  desc:'Children · Education · Creativity'},
  {num:6,  name:'Shatru',  desc:'Enemies · Health · Service'},
  {num:7,  name:'Yuvati',  desc:'Marriage · Partnership · Desires'},
  {num:8,  name:'Mrityu',  desc:'Transformation · Occult · Longevity'},
  {num:9,  name:'Dharma',  desc:'Religion · Fortune · Father'},
  {num:10, name:'Karma',   desc:'Career · Status · Authority'},
  {num:11, name:'Labh',    desc:'Gains · Aspirations · Friends'},
  {num:12, name:'Vyaya',   desc:'Loss · Moksha · Foreign Lands'},
];

export const getRashi = (lagnaRashi, houseNum) => {
  if (!lagnaRashi) return null;
  return RASHIS[((lagnaRashi - 1 + houseNum - 1) % 12)];
};

// ── Lo Shu calculation ────────────────────────────────────────────────────────
// Not just the raw DOB digits — Moolank (birth number, from the day) and
// Bhagyank (destiny number, from the full DOB) are ALSO placed into the
// grid. A number derived only from Moolank/Bhagyank (never appearing bare
// in the DOB) still counts as present — e.g. DOB 16-10-2007 has no 8 among
// its own digits, but Bhagyank (1+6+1+0+2+0+0+7=17→8) puts an 8 in the
// grid, so 8 is NOT one of the missing numbers.
const calcLoShu = (dob) => {
  if (!dob) return { present: [], missing: [1,2,3,4,5,6,7,8,9] };
  const dobDigits = dob.replace(/\D/g, '').split('').map(Number).filter(n => n >= 1 && n <= 9);
  const moolank = calcMoolank(dob);
  const bhagyank = calcBhagyank(dob);
  const allDigits = [...dobDigits, ...(moolank ? [moolank] : []), ...(bhagyank ? [bhagyank] : [])];
  const present = [...new Set(allDigits)].sort((a, b) => a - b);
  return { present, missing: [1,2,3,4,5,6,7,8,9].filter(n => !present.includes(n)) };
};

// ── PDF type scale — every report exported from this file uses exactly these
// 3 sizes (Title/Subtitle/Description = 25/20/16pt). Title keeps each
// report's own theme accent colour; Subtitle and Description are ALWAYS
// black, no matter what the section used to be coloured.
const TYPE_TITLE_SIZE = 25;
const TYPE_SUBTITLE_SIZE = 20;
const TYPE_DESCRIPTION_SIZE = 16;
const TYPE_BLACK = [0, 0, 0];

// Identifies the birth details a chart was generated from (compared later to spot a stale chart).
const birthKeyOf = (p) => [p.dob || '', p.tob || '', p.place || ''].join('|');

const newPerson = () => ({ id: Date.now() + Math.random(), name: '', gender: 'Male', dob: '', tob: '', place: '', placeGeo: null, details: '' });
const newAstroPersonData = () => ({ lagnaRashi: 0, houses: {}, ascendantNormDegree: 0, moonNakshatraNumber: 0, vimshottari: null, ashtakavarga: null, hitTheory: null, richHtml: '', suggestionsHtml: '' });

// ── LoShuGrid ─────────────────────────────────────────────────────────────────
function LoShuGrid({ dob, language = 'en' }) {
  const isMobile = useIsMobile();
  const { present, missing } = calcLoShu(dob);
  return (
    <div>
      {/* Grid cells are a fixed 120px each (360px+gaps total) — wider than a
          ~360-400px phone viewport. Rather than redesign the grid's cell
          sizing, the wrapper below just scrolls horizontally on mobile
          (same approach as the astro chart pane) instead of blowing out
          the page; desktop keeps overflow:visible (its normal default). */}
      <div style={{ overflowX: isMobile ? 'auto' : 'visible', WebkitOverflowScrolling: isMobile ? 'touch' : undefined }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 120px)', gap: '6px' }}>
        {LO_SHU_GRID.flat().map(num => {
          const d = LO_SHU_DATA[num];
          const isMissing = missing.includes(num);
          return (
            <div key={num} style={{
              background: d.color, borderRadius: '10px', width: '120px', height: '120px',
              position: 'relative', display: 'flex', flexDirection: 'column',
              alignItems: 'center', padding: '10px 6px 6px', overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.22)'
            }}>
              <div style={{ color: '#fff', fontWeight: 900, fontSize: '2.2rem', lineHeight: 1 }}>{localDigits(num, language)}</div>
              <div style={{ color: 'rgba(255,255,255,0.95)', fontSize: '0.6rem', fontWeight: 700, textAlign: 'center', lineHeight: 1.3, marginTop: '4px' }}>{loShuText('direction', d.direction, language)}</div>
              <div style={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.56rem', textAlign: 'center', lineHeight: 1.4, marginTop: '3px' }}>{loShuText('planet', d.planet, language)}<br />{loShuText('element', d.element, language)}</div>
              <div style={{ color: 'rgba(255,255,255,0.95)', fontSize: '0.56rem', fontWeight: 700, textAlign: 'center', lineHeight: 1.3, marginTop: '3px' }}>{loShuText('use', d.use, language)}</div>
              {isMissing && (
                <svg viewBox="0 0 130 130" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                  <line x1="10" y1="10" x2="120" y2="120" stroke="#FF1111" strokeWidth="10" strokeLinecap="round" />
                  <line x1="120" y1="10" x2="10" y2="120" stroke="#FF1111" strokeWidth="10" strokeLinecap="round" />
                </svg>
              )}
            </div>
          );
        })}
      </div>
      </div>
      <div style={{ marginTop: '8px', fontSize: '11px', lineHeight: 1.6 }}>
        <span style={{ color: '#16a34a', fontWeight: 700 }}>✓ {nvLabel('present', language)}: {present.length ? present.map(n => localDigits(n, language)).join(', ') : '—'}</span><br />
        <span style={{ color: '#dc2626', fontWeight: 700 }}>✗ {nvLabel('missing', language)}: {missing.length ? missing.map(n => localDigits(n, language)).join(', ') : '—'}</span>
      </div>
    </div>
  );
}

// ── PersonFormCard (gateway view — no Lo Shu preview) ─────────────────────────
function PersonFormCard({ person, index, onChange, onRemove, canRemove, showGrid = false, language = 'en' }) {
  // Mobile: the 2-col field grid below is already fractional (1fr 1fr), so
  // it never forces horizontal overflow — but each field's own label makes
  // a 2-up layout cramped on a phone. Collapsing to a single column keeps
  // the exact same per-field labeled markup (no JSX duplication needed,
  // unlike BirthDateModal's unlabeled desktop row) while giving each field
  // its full width to breathe. Desktop keeps the original 2-col grid.
  const isMobile = useIsMobile();
  const set = (field, val) => onChange(person.id, field, val);
  const inputSt = (extra = {}) => ({
    width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: '6px',
    fontSize: '13px', outline: 'none', background: '#fff', color: '#0f172a',
    boxSizing: 'border-box', ...extra
  });
  const labelSt = { display: 'block', marginBottom: '4px', fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };

  return (
    <div style={{ background: '#fff', borderRadius: '12px', border: '1.5px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden', marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: isMobile ? '10px 12px' : '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: '#00A19D', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '14px', flexShrink: 0 }}>{index + 1}</div>
        <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{person.name || `Person ${index + 1}`}</div>
        {canRemove && (
          <button onClick={() => onRemove(person.id)} style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600, flexShrink: 0 }}>
            <Trash2 size={13} />{!isMobile && ' Remove'}
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: isMobile ? '16px' : '24px', padding: isMobile ? '14px' : '18px 20px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '12px' : '14px', marginBottom: '14px' }}>
            <div>
              <label style={labelSt}>Full Name *</label>
              <input style={inputSt()} placeholder="Enter full name" value={person.name}
                onChange={e => set('name', e.target.value)}
                onFocus={e => e.target.style.borderColor = '#00A19D'}
                onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
            </div>
            <div>
              <label style={labelSt}>Gender</label>
              <div style={{ display: 'flex', gap: '12px', paddingTop: '9px' }}>
                {['Male', 'Female', 'Other'].map(g => (
                  <label key={g} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', fontSize: '13px', color: '#334155', userSelect: 'none' }}>
                    <input type="radio" name={`vpg-g-${person.id}`} value={g} checked={person.gender === g} onChange={() => set('gender', g)} style={{ accentColor: '#00A19D' }} />
                    {g}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label style={labelSt}>Date of Birth *</label>
              <input type="date" style={inputSt()} value={person.dob} onChange={e => set('dob', e.target.value)} />
            </div>
            <div>
              <label style={labelSt}>Time of Birth</label>
              <input type="time" style={inputSt()} value={person.tob || ''} onChange={e => set('tob', e.target.value)} />
            </div>
            <div>
              <label style={labelSt}>Place of Birth</label>
              <PlaceAutocomplete
                style={inputSt()}
                placeholder="Type city, then pick from the list"
                value={person.place}
                geo={person.placeGeo}
                onTextChange={text => { set('place', text); set('placeGeo', null); }}
                onSelect={p => { set('place', p.label); set('placeGeo', toPlaceGeo(p)); }}
              />
            </div>
          </div>
          <div>
            <label style={labelSt}>Additional Birth Details</label>
            <textarea style={inputSt({ resize: 'vertical', minHeight: '62px' })}
              placeholder="Hospital, any other details..."
              value={person.details} onChange={e => set('details', e.target.value)}
              onFocus={e => e.target.style.borderColor = '#00A19D'}
              onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
          </div>
        </div>
        {showGrid && (
          <div style={isMobile ? { flexShrink: 0, width: '100%' } : { flexShrink: 0 }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Lo Shu Grid Preview</div>
            <LoShuGrid dob={person.dob} language={language} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── RichEditor ────────────────────────────────────────────────────────────────
const RichEditor = React.memo(function RichEditor({ itemId, value, onChange }) {
  const editorRef  = useRef(null);
  const lastValRef = useRef(null);
  useEffect(() => {
    if (editorRef.current && value !== lastValRef.current) {
      // Truly empty (not '<p><br></p>') so the :empty CSS placeholder rule
      // below can actually match — a filler <p> always counts as a child,
      // so :empty never fires once one's been inserted, and the box just
      // looks blank/broken with nothing telling the user it's editable.
      editorRef.current.innerHTML = value || '';
      lastValRef.current = value;
    }
  }, [value, itemId]);
  const handleInput = useCallback(() => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      lastValRef.current = html;
      onChange(itemId, html);
    }
  }, [itemId, onChange]);
  const execCmd = (cmd) => { editorRef.current?.focus(); document.execCommand(cmd, false, null); handleInput(); };
  return (
    <div>
      <div style={{ display: 'flex', gap: '4px', padding: '4px 8px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
        {[['bold', 'B'], ['italic', 'I'], ['underline', 'U']].map(([cmd, lbl]) => (
          <button key={cmd} onMouseDown={e => { e.preventDefault(); execCmd(cmd); }}
            style={{ padding: '2px 8px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '3px', fontSize: '11px', fontWeight: cmd === 'bold' ? 700 : 400, fontStyle: cmd === 'italic' ? 'italic' : 'normal', cursor: 'pointer', color: '#334155' }}>
            {lbl}
          </button>
        ))}
      </div>
      <div ref={editorRef} contentEditable suppressContentEditableWarning className="space-editor" onInput={handleInput}
        data-placeholder="Type your analysis notes here…"
        style={{ minHeight: '120px', padding: '10px', outline: 'none', fontSize: '0.82rem', lineHeight: 1.6, color: '#1e293b', wordBreak: 'break-word' }} />
    </div>
  );
});

// ── KuaCard ───────────────────────────────────────────────────────────────────
const KuaCard = React.memo(function KuaCard({ item, onHtmlChange, language = 'en' }) {
  const kua = item.kuaNumber;
  const kd  = kua ? KUA_DATA[kua] : null;
  // See the [language] effect that regenerates this on a language switch —
  // hand-edited content is intentionally left alone, so this badge is the
  // only signal the user gets.
  const isEdited = !isDefaultKuaHtml(item, item.richHtml);
  return (
    <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: '#00A19D', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '14px', flexShrink: 0 }}>{kua ?? '#'}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a' }}>{item.personName}</div>
          {kd && <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '1px' }}>{kuaLabel('kuaNumber', language)} {kua} · {kuaGroup(kd.group, language)} {kuaLabel('group', language)} · {kuaElement(kd.element, language)}</div>}
        </div>
        {isEdited && (
          <span
            title="Edited — won't auto-translate when you switch language. Edit it again in each language you need."
            style={{ fontSize: '10px', color: '#b45309', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, padding: '2px 6px', fontWeight: 700, flexShrink: 0 }}
          >
            Edited
          </span>
        )}
      </div>
      <RichEditor itemId={item.id} value={item.richHtml} onChange={onHtmlChange} />
    </div>
  );
});

// ── KuaComparisonTable — side-by-side profile/numerology/direction table ───
// One column per person, matching the standard "Kua Number Analysis
// Comparison" layout (Profile → Numerology → Favourable Direction →
// Challenging Direction), each computed live from that person's DOB/gender.
function KuaComparisonTable({ persons, language = 'en' }) {
  const rows = persons.map(p => {
    const kua      = calcKuaNumber(p.dob, p.gender);
    const moolank  = calcMoolank(p.dob);
    const bhagyank = calcBhagyank(p.dob);
    const kd       = kua ? KUA_DATA[kua] : null;
    const match    = calcNumerologyMatch(moolank, bhagyank);
    return {
      name: p.name || 'Person', kua, kd, moolank, bhagyank, match,
      luckyColors:   kuaColors(getLuckyColors(moolank, bhagyank), language),
      unluckyColors: kuaColors(getUnluckyColors(moolank, bhagyank), language),
    };
  });
  const T = (k) => kuaLabel(k, language);

  const th = { padding: '9px 12px', fontSize: '11.5px', fontWeight: 700, color: '#0f172a', textAlign: 'left', borderBottom: '1.5px solid #e2e8f0', whiteSpace: 'nowrap', background: '#fff', position: 'sticky', top: 0 };
  const tdLabel = { padding: '9px 12px', fontSize: '11px', fontWeight: 600, color: '#64748b', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap', background: '#f8fafc' };
  const td = { padding: '9px 12px', fontSize: '12px', color: '#1e293b', borderBottom: '1px solid #f1f5f9' };
  const sectionRow = (label) => (
    <tr><td colSpan={persons.length + 1} style={{ padding: '10px 12px 4px', fontSize: '10.5px', fontWeight: 800, color: '#00857E', textTransform: 'uppercase', letterSpacing: '0.06em', background: '#f0fdfa', borderTop: '2px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>{label}</td></tr>
  );
  const dataRow = (label, getVal) => (
    <tr>
      <td style={tdLabel}>{label}</td>
      {rows.map((r, i) => <td key={i} style={td}>{getVal(r) ?? '—'}</td>)}
    </tr>
  );

  return (
    <div style={{ background: '#fff', borderRadius: '10px', border: '1.5px solid #e2e8f0', overflow: 'auto', marginBottom: '20px' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: `${260 + persons.length * 160}px` }}>
        <thead>
          <tr>
            <th style={{ ...th, background: '#f8fafc' }}>Attribute</th>
            {rows.map((r, i) => <th key={i} style={th}>{r.name}</th>)}
          </tr>
        </thead>
        <tbody>
          {sectionRow(T('profile'))}
          {dataRow(T('kuaNumber'), r => r.kua)}
          {dataRow(T('group'), r => r.kd ? kuaGroup(r.kd.group, language) : null)}
          {dataRow(T('element'), r => r.kd ? kuaElement(r.kd.element, language) : null)}
          {dataRow(T('personality'), r => r.kd ? kuaPersonality(r.kua, language) : null)}
          {dataRow(T('lifeArea'), r => r.kd ? kuaLifeArea(r.kua, language) : null)}

          {sectionRow(T('numerology'))}
          {dataRow(T('moolank'), r => r.moolank)}
          {dataRow(T('bhagyank'), r => r.bhagyank)}
          {dataRow(T('luckyNumbers'), r => r.match?.lucky.join(', '))}
          {dataRow(T('neutralNumbers'), r => r.match?.neutral.join(', '))}
          {dataRow(T('unluckyNumbers'), r => r.match?.unlucky.join(', '))}
          {dataRow(T('luckyColors'), r => r.luckyColors)}
          {dataRow(T('unluckyColors'), r => r.unluckyColors)}

          {sectionRow(T('favourableDirection'))}
          {dataRow(T('shengChi'), r => r.kd ? kuaDir(r.kd.favorable.shengChi, language) : null)}
          {dataRow(T('tienYi'), r => r.kd ? kuaDir(r.kd.favorable.tianYi, language) : null)}
          {dataRow(T('nienYen'), r => r.kd ? kuaDir(r.kd.favorable.nienYen, language) : null)}
          {dataRow(T('fuWei'), r => r.kd ? kuaDir(r.kd.favorable.fuWei, language) : null)}

          {sectionRow(T('challengingDirection'))}
          {dataRow(T('hoHai'), r => r.kd ? kuaDir(r.kd.challenging.hoHai, language) : null)}
          {dataRow(T('wuGui'), r => r.kd ? kuaDir(r.kd.challenging.wuGui, language) : null)}
          {dataRow(T('liuSha'), r => r.kd ? kuaDir(r.kd.challenging.liuSha, language) : null)}
          {dataRow(T('jueMing'), r => r.kd ? kuaDir(r.kd.challenging.jueMing, language) : null)}
        </tbody>
      </table>
    </div>
  );
}

// ── D1 Chart + its 3 relabeled variants — same geometry, same computed
// Ascendant/planet data throughout; only the label shown per house changes
// (see NorthIndianChart.jsx for the shared geometry, VastuChartVariants.js
// for the 3 direction tables). Laid out 2×2 like the reference: D1 chart is
// the only one that's click-to-edit (it drives the house/planet editor
// panel) — the other three are read-only relabeled views of the same data.
function D1Chart({ lagnaRashi, houses, ascendantNormDegree, selectedHouse, onSelectHouse, language = 'en', chartStyles = { north: true, south: true }, onToggleStyle = null }) {
  const miniSize = 440;
  const astroVastuLabels = computeAstroVastuDirections(ascendantNormDegree || 0);
  // Translate each variant's own FIXED direction/wall vocabulary into the
  // selected report language — the chart's actual Ascendant/planet
  // placements come straight from the navamsha.in API (which this app never
  // asks for a language, and which returns numeric/structural data anyway,
  // not prose), so those are unaffected; this only covers the labels this
  // app itself draws around them. See compassCodeLabel/laalKitaabLabel's
  // own comments in KundaliReportsData.js for exactly what is and isn't
  // translated (the 8 primary directions are; the finer 16-point-only codes
  // like "ENE" are deliberately left in English — no reliable compound
  // translation exists to draw from for those).
  const translateLabels = (houseLabels, translator) => {
    if (!houseLabels) return null;
    const out = {};
    Object.keys(houseLabels).forEach((h) => { out[h] = translator(houseLabels[h], language); });
    return out;
  };
  // Text size bumped to 1.5x current per explicit request (live UI only —
  // the PDF export's own chartVariants array further down is untouched).
  const variants = [
    { title: 'Chart D1',                  houseLabels: null,                                                    fontSize: 17.25 },
    { title: 'Vedic Vastu Chart',         houseLabels: translateLabels(VEDIC_VASTU_DIRECTIONS, compassCodeLabel), fontSize: 13.5 },
    { title: 'AstroVastu Chart',          houseLabels: translateLabels(astroVastuLabels, compassCodeLabel),      fontSize: 9.75 },
    { title: 'Laal Kitaab Makaan Chart',  houseLabels: translateLabels(LAAL_KITAAB_DIRECTIONS, laalKitaabLabel), fontSize: 9.75 },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
      {/* Style switch — the report prints only the kundli style(s) ticked here (one must stay on). */}
      {onToggleStyle && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Kundli style in report</span>
          {[['north', 'North Indian'], ['south', 'South Indian']].map(([key, label]) => {
            const on = chartStyles[key];
            const onlyOne = on && !(key === 'north' ? chartStyles.south : chartStyles.north);
            return (
              <button key={key} type="button" onClick={() => onToggleStyle(key)}
                title={onlyOne ? 'At least one style must stay on' : (on ? `Hide ${label} chart from the report` : `Show ${label} chart in the report`)}
                style={{ padding: '5px 12px', borderRadius: '16px', border: on ? '2px solid #00A19D' : '1.5px solid #cbd5e1', background: on ? '#e6f7f7' : '#f8fafc', color: on ? '#00A19D' : '#64748b', fontSize: '12px', fontWeight: 700, cursor: onlyOne ? 'not-allowed' : 'pointer', opacity: onlyOne ? 0.85 : 1 }}>
                {on ? '✓ ' : ''}{label}
              </button>
            );
          })}
        </div>
      )}
      {chartStyles.north && (
      <div>
        <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>North Indian Chart</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px' }}>
          {variants.map((v, i) => (
            <div key={v.title} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '12px' }}>
              <div style={{ width: '100%', fontSize: '13px', fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' }}>{v.title}</div>
              <NorthIndianChartSVG
                lagnaRashi={lagnaRashi}
                houses={houses}
                selectedHouse={i === 0 ? selectedHouse : null}
                onSelectHouse={i === 0 ? onSelectHouse : undefined}
                getRashi={getRashi}
                size={miniSize}
                houseLabels={v.houseLabels}
                labelFontSize={v.fontSize}
              />
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Same 4 charts again, just drawn in the South Indian ("box") style —
          fixed Rashi cells, house numbers move around the Ascendant instead
          of the Rashi names. Same underlying data/selection, just a
          different design per the client's reference image. */}
      {chartStyles.south && (
      <div>
        <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>South Indian Chart</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px' }}>
          {variants.map((v, i) => (
            <div key={v.title} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '12px' }}>
              <div style={{ width: '100%', fontSize: '13px', fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' }}>{v.title}</div>
              <SouthIndianChartSVG
                lagnaRashi={lagnaRashi}
                houses={houses}
                selectedHouse={i === 0 ? selectedHouse : null}
                onSelectHouse={i === 0 ? onSelectHouse : undefined}
                size={miniSize}
                houseLabels={v.houseLabels}
                labelFontSize={v.fontSize}
              />
            </div>
          ))}
        </div>
      </div>
      )}
    </div>
  );
}

// ── Kundali report sections — right-side nav + content ─────────────────────
// All 10 of the client's requested report sections are now wired up.
// "Hit Theory" was the last holdout — earlier thought to be an
// unreproducible proprietary paid-course technique, until the client
// supplied the actual geometry behind their own reference report (directed
// planetary/cusp angles classified against a standard 10-angle aspect
// table with an ~8° orb, computed from navamsha.in's KP chart — see
// KundaliReportsData.js's HIT_MASTER/generatePlanetPlanetGrid/
// generatePlanetHouseGrid and this file's HitTheorySection below). See
// KundaliReportsData.js's file header for the sourcing notes on Ruling
// Deities / Astro-Vastu Plant Remedy / Best Direction Table too — those
// use the standard classical tables rather than a confirmed match to the
// client's own reference software.
const KUNDALI_SECTIONS = [
  { key: 'kundaliCharts',  label: 'Kundali',                            ready: true },
  { key: 'vimsottari',     label: 'Vimsottari Table',                   ready: true },
  { key: 'navatara',       label: 'Navatara Chakra',                    ready: true },
  { key: 'rulingDeities',  label: 'Ruling Deities',                     ready: true },
  { key: 'plantRemedy',    label: 'Astro-Vastu Plant Remedy',           ready: true },
  { key: 'houseMapping',   label: 'General House Activity Mapping',     ready: true },
  { key: 'officeMapping',  label: 'Office/Shop/Factory Layout Mapping', ready: true },
  { key: 'hitTheory',      label: 'Hit Theory',                         ready: true },
  { key: 'bestDirection',  label: 'Best Direction Table',                ready: true },
  { key: 'suggestions',    label: 'General Suggestions & Remedies',     ready: true },
];

const formatDashaDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const kundaliTableCellStyle = { padding: '8px 10px', fontSize: '12px', color: '#334155', borderBottom: '1px solid #eef1f5' };
const kundaliTableHeadStyle = { ...kundaliTableCellStyle, textAlign: 'left', fontWeight: 700, color: '#0f172a', background: '#f8fafc', textTransform: 'uppercase', fontSize: '10.5px', letterSpacing: '0.03em' };

function VimsottariTableSection({ vimshottari, language = 'en' }) {
  if (!vimshottari?.mahadashas?.length) {
    return <EmptyKundaliNote text="Generate the chart above first — the Dasha timeline is calculated from the same birth details." />;
  }
  return (
    <div>
      <p style={{ fontSize: '11.5px', color: '#64748b', margin: '0 0 10px' }}>
        Full Vimshottari Mahadasha timeline, from birth through the classical 120-year cycle.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={kundaliTableHeadStyle}>{kundaliTextLabel('Planet', language)}</th><th style={kundaliTableHeadStyle}>{kundaliTextLabel('Start', language)}</th><th style={kundaliTableHeadStyle}>{kundaliTextLabel('End', language)}</th></tr></thead>
        <tbody>
          {vimshottari.mahadashas.map((md, i) => (
            <tr key={i}>
              <td style={{ ...kundaliTableCellStyle, fontWeight: 700 }}>{planetLabel(md.lord, language)}</td>
              <td style={kundaliTableCellStyle}>{formatDashaDate(md.start)}</td>
              <td style={kundaliTableCellStyle}>{formatDashaDate(md.end)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NavataraChakraSection({ moonNakshatraNumber, language = 'en' }) {
  const rows = computeNavataraChakra(moonNakshatraNumber);
  if (!rows.length) {
    return <EmptyKundaliNote text="Generate the chart above first — the Navatara Chakra is calculated from the birth (Moon) Nakshatra." />;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
        <thead>
          <tr>
            <th style={kundaliTableHeadStyle}>{kundaliTextLabel('S.No.', language)}</th>
            <th style={kundaliTableHeadStyle}>{kundaliTextLabel('Navatara', language)}</th>
            <th style={kundaliTableHeadStyle}>{kundaliTextLabel('Lord', language)}</th>
            <th style={kundaliTableHeadStyle}>{kundaliTextLabel('Nakshatra 1', language)}</th>
            <th style={kundaliTableHeadStyle}>{kundaliTextLabel('Nakshatra 2', language)}</th>
            <th style={kundaliTableHeadStyle}>{kundaliTextLabel('Nakshatra 3', language)}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td style={kundaliTableCellStyle}>{r.number}</td>
              <td style={{ ...kundaliTableCellStyle, fontWeight: 700 }}>{navataraLabel(r.key, language)}</td>
              <td style={kundaliTableCellStyle}>{planetLabel(r.lord, language)}</td>
              <td style={kundaliTableCellStyle}>{r.nakshatras[0]}</td>
              <td style={kundaliTableCellStyle}>{r.nakshatras[1]}</td>
              <td style={kundaliTableCellStyle}>{r.nakshatras[2]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Ruling Deities — one classical devata per birth (Moon) Nakshatra. Simple
// deterministic lookup, same shape as NavataraChakraSection above.
function RulingDeitySection({ moonNakshatraNumber, language = 'en' }) {
  if (!moonNakshatraNumber) {
    return <EmptyKundaliNote text="Generate the chart above first — the Ruling Deity is looked up from the birth (Moon) Nakshatra." />;
  }
  const nak = getNakshatra(moonNakshatraNumber);
  const deity = deityLabel(getNakshatraDeity(moonNakshatraNumber), language);
  return (
    <div>
      <p style={{ fontSize: '11.5px', color: '#64748b', margin: '0 0 14px' }}>
        The presiding deity (devata) of the birth Nakshatra, per classical Vedic astrology.
      </p>
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 160px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '14px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>{kundaliTextLabel('Birth Nakshatra', language)}</div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>{nak.name}</div>
        </div>
        <div style={{ flex: '1 1 160px', background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '8px', padding: '14px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: '#00857E', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>{kundaliTextLabel('Ruling Deity', language)}</div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#00857E' }}>{deity}</div>
        </div>
      </div>
    </div>
  );
}

// Astro-Vastu Plant Remedy — highlights the remedy for the birth Nakshatra's
// ruling planet, plus the full classical Navagraha Vatika reference table
// for context (all 9 planets, so the astrologer can also recommend a
// remedy for any OTHER planet that shows up weak/afflicted elsewhere in
// the chart, not just the birth Nakshatra's own lord).
function PlantRemedySection({ moonNakshatraNumber, language = 'en' }) {
  if (!moonNakshatraNumber) {
    return <EmptyKundaliNote text="Generate the chart above first — the highlighted remedy is based on the birth (Moon) Nakshatra's ruling planet." />;
  }
  const nak = getNakshatra(moonNakshatraNumber);
  const ownRemedy = GRAHA_PLANT_REMEDY[nak.lord];
  return (
    <div>
      <p style={{ fontSize: '11.5px', color: '#64748b', margin: '0 0 14px' }}>
        Classical Navagraha Vatika remedy — a specific tree/plant and Vastu direction for each planet.
      </p>
      {ownRemedy && (
        <div style={{ background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: '#00857E', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
            Recommended for this person — {planetLabel(nak.lord, language)} (Birth Nakshatra Lord)
          </div>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{plantLabel(nak.lord, language)}</div>
          <div style={{ fontSize: '12px', color: '#334155', marginTop: '2px' }}>Place in: <strong>{directionLabel(ownRemedy.direction, language)}</strong></div>
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '480px' }}>
          <thead>
            <tr>
              <th style={kundaliTableHeadStyle}>{kundaliTextLabel('Planet', language)}</th>
              <th style={kundaliTableHeadStyle}>{kundaliTextLabel('Remedy Plant / Tree', language)}</th>
              <th style={kundaliTableHeadStyle}>{kundaliTextLabel('Vastu Direction', language)}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(GRAHA_PLANT_REMEDY).map(([planet, r]) => (
              <tr key={planet} style={planet === nak.lord ? { background: '#f0fdfa' } : undefined}>
                <td style={{ ...kundaliTableCellStyle, fontWeight: 700 }}>{planetLabel(planet, language)}</td>
                <td style={kundaliTableCellStyle}>{plantLabel(planet, language)}</td>
                <td style={kundaliTableCellStyle}>{directionLabel(r.direction, language)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Best Direction Table — the birth Nakshatra's classical best house-facing
// direction, plus the full 27-Nakshatra reference table for context.
function BestDirectionSection({ moonNakshatraNumber, language = 'en' }) {
  if (!moonNakshatraNumber) {
    return <EmptyKundaliNote text="Generate the chart above first — the Best Direction is looked up from the birth (Moon) Nakshatra." />;
  }
  const nak = getNakshatra(moonNakshatraNumber);
  const direction = directionLabel(getNakshatraBestDirection(moonNakshatraNumber), language);
  return (
    <div>
      <p style={{ fontSize: '11.5px', color: '#64748b', margin: '0 0 14px' }}>
        Classical best house-facing direction for this person's birth Nakshatra (cardinal directions only).
      </p>
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div style={{ flex: '1 1 160px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '14px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>{kundaliTextLabel('Birth Nakshatra', language)}</div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>{nak.name}</div>
        </div>
        <div style={{ flex: '1 1 160px', background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '8px', padding: '14px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: '#00857E', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>{kundaliTextLabel('Best Direction', language)}</div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#00857E' }}>{direction}</div>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '360px' }}>
          <thead>
            <tr><th style={kundaliTableHeadStyle}>{kundaliTextLabel('Nakshatra', language)}</th><th style={kundaliTableHeadStyle}>{kundaliTextLabel('Best Direction', language)}</th></tr>
          </thead>
          <tbody>
            {Array.from({ length: 27 }, (_, i) => i + 1).map((n) => (
              <tr key={n} style={n === moonNakshatraNumber ? { background: '#f0fdfa' } : undefined}>
                <td style={{ ...kundaliTableCellStyle, fontWeight: 700 }}>{getNakshatra(n).name}</td>
                <td style={kundaliTableCellStyle}>{directionLabel(getNakshatraBestDirection(n), language)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Hit Theory — directed-angle aspect grids between the 9 planets and
// between each planet and each house cusp. See KundaliReportsData.js's
// HIT_MASTER/calculateHit for the geometry itself; this component is just
// the two grids' presentation, toggled via a small sub-tab (both grids are
// wide enough that showing both stacked at once would be a lot of
// scrolling for not much benefit).
const HIT_CELL_BG = { positive: '#f0fdf4', negative: '#fef2f2', neutral: '#f0f9ff' };
const HIT_CELL_FG = { positive: '#15803d', negative: '#b91c1c', neutral: '#0369a1' };

function HitCell({ value, language = 'en' }) {
  if (!value) {
    return <td style={{ ...kundaliTableCellStyle, textAlign: 'center', color: '#cbd5e1' }}>—</td>;
  }
  const hit = value.hit;
  return (
    <td style={{
      ...kundaliTableCellStyle, textAlign: 'center', padding: '5px 6px',
      background: hit ? HIT_CELL_BG[hit.category] : undefined,
    }}>
      <div>{value.displayAngle}°</div>
      {hit && <strong style={{ color: HIT_CELL_FG[hit.category], fontSize: '10.5px' }} title={hitNameLabel(hit.code, language)}>{hit.code}</strong>}
    </td>
  );
}

function PlanetPlanetGrid({ grid, language = 'en' }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '620px' }}>
        <thead>
          <tr>
            <th style={kundaliTableHeadStyle}>{kundaliTextLabel('Source \\ Target', language)}</th>
            {HIT_PLANETS.map((p) => <th key={p} style={kundaliTableHeadStyle}>{planetLabel(p, language)}</th>)}
          </tr>
        </thead>
        <tbody>
          {HIT_PLANETS.map((source) => (
            <tr key={source}>
              <td style={{ ...kundaliTableCellStyle, fontWeight: 700 }}>{planetLabel(source, language)}</td>
              {HIT_PLANETS.map((target) => <HitCell key={target} value={grid[source]?.[target]} language={language} />)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlanetHouseGrid({ grid, cusps, language = 'en' }) {
  const houseMeta = {};
  HIT_DISPLAY_HOUSES.forEach((h) => { if (cusps[h] != null) houseMeta[h] = getZodiacFromLongitude(cusps[h]); });
  const metaRow = (label, get) => (
    <tr>
      <th style={{ ...kundaliTableHeadStyle, textAlign: 'left' }}>{label}</th>
      {HIT_DISPLAY_HOUSES.map((h) => <th key={h} style={kundaliTableHeadStyle}>{houseMeta[h] ? get(houseMeta[h]) : '—'}</th>)}
    </tr>
  );
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
        <thead>
          {metaRow(kundaliTextLabel('Direction', language), (m) => m.direction)}
          {metaRow(kundaliTextLabel('Zodiac', language), (m) => m.sign)}
          {metaRow(kundaliTextLabel('Owner', language), (m) => m.owner)}
          <tr>
            <th style={{ ...kundaliTableHeadStyle, textAlign: 'left' }}>{kundaliTextLabel('House', language)}</th>
            {HIT_DISPLAY_HOUSES.map((h) => <th key={h} style={kundaliTableHeadStyle}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {HIT_PLANETS.map((planet) => (
            <tr key={planet}>
              <td style={{ ...kundaliTableCellStyle, fontWeight: 700 }}>{planetLabel(planet, language)}</td>
              {HIT_DISPLAY_HOUSES.map((h) => <HitCell key={h} value={grid[planet]?.[h]} language={language} />)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HitTheorySection({ hitTheory, language = 'en' }) {
  const [view, setView] = useState('planetToPlanet');
  if (!hitTheory?.planets || !hitTheory?.cusps) {
    return <EmptyKundaliNote text="Generate the chart above first — Hit Theory is calculated from the KP chart's planet and house-cusp longitudes." />;
  }
  const tabBtn = (key, label) => (
    <button onClick={() => setView(key)} style={{
      padding: '6px 14px', borderRadius: '6px', border: '1px solid ' + (view === key ? '#00A19D' : '#e2e8f0'),
      background: view === key ? '#00A19D' : '#fff', color: view === key ? '#fff' : '#334155',
      fontSize: '11.5px', fontWeight: 700, cursor: 'pointer',
    }}>{label}</button>
  );
  return (
    <div>
      <p style={{ fontSize: '11.5px', color: '#64748b', margin: '0 0 10px' }}>
        Directed-angle aspects (Conjunction/Square/Trine/Opposition, etc.) between the 9 planets and the 12 house cusps, ±8° orb.
      </p>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        {tabBtn('planetToPlanet', `${kundaliTextLabel('Planet', language)} ↔ ${kundaliTextLabel('Planet', language)}`)}
        {tabBtn('planetToHouse', `${kundaliTextLabel('Planet', language)} ↔ ${kundaliTextLabel('House', language)}`)}
      </div>
      {view === 'planetToPlanet'
        ? <PlanetPlanetGrid grid={hitTheory.planetToPlanet} language={language} />
        : <PlanetHouseGrid grid={hitTheory.planetToHouse} cusps={hitTheory.cusps} language={language} />}
    </div>
  );
}

function ActivityMappingSection({ ashtakavarga, lagnaRashi, mode, language = 'en' }) {
  if (!ashtakavarga?.bav) {
    return <EmptyKundaliNote text="Generate the chart above first — this mapping is calculated from Ashtakavarga strength data." />;
  }
  const rows = buildActivityMapping(ashtakavarga, lagnaRashi, mode);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '520px' }}>
        <thead>
          <tr>
            <th style={kundaliTableHeadStyle}>{kundaliTextLabel('Planet', language)}</th>
            <th style={kundaliTableHeadStyle}>{kundaliTextLabel('Activity', language)}</th>
            <th style={kundaliTableHeadStyle}>{directionLabel('East', language)} (1,5,9)</th>
            <th style={kundaliTableHeadStyle}>{directionLabel('West', language)} (3,7,11)</th>
            <th style={kundaliTableHeadStyle}>{directionLabel('North', language)} (4,8,12)</th>
            <th style={kundaliTableHeadStyle}>{directionLabel('South', language)} (2,6,10)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.planet} style={r.planet === 'Sarvastak_Varga' ? { background: '#f0fdfa' } : undefined}>
              <td style={{ ...kundaliTableCellStyle, fontWeight: 700 }}>{planetLabel(r.planet, language)}{r.isApproximated ? ' *' : ''}</td>
              {/* The ASC row's own activity text ("Main Door"/"Main Entry / Plot
                  Facing") isn't one of ACTIVITY_LABELS' keys — it has its own
                  dedicated translator (ascLabel) — so activityLabel alone left
                  it silently untranslated in every language before this. */}
              <td style={kundaliTableCellStyle}>{r.planet === 'ASC' ? ascLabel(mode, language) : r.activities.map(a => activityLabel(a, language)).join(', ')}</td>
              <td style={kundaliTableCellStyle}>{r.east}</td>
              <td style={kundaliTableCellStyle}>{r.west}</td>
              <td style={kundaliTableCellStyle}>{r.north}</td>
              <td style={kundaliTableCellStyle}>{r.south}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ fontSize: '10.5px', color: '#94a3b8', marginTop: '8px' }}>
        * ASC row is approximated using the Ascendant's own ruling planet (Lagna Lord) — Ascendant doesn't have a
        standard Bhinnashtakavarga table of its own in classical Vedic astrology.
      </p>
    </div>
  );
}

// General Suggestions & Remedies — the reference report's own version of
// this section is empty "Remedy" placeholder bullets meant for the
// astrologer to fill in by hand, so this is a free-text field (same
// RichEditor already used for Analysis Notes) rather than a formula.
function GeneralSuggestionsSection({ personId, value, onChange }) {
  return (
    <div>
      <p style={{ fontSize: '11.5px', color: '#64748b', margin: '0 0 10px' }}>
        Type any general Vastu suggestions or crystal/space-energy remedies for this person — printed as-is in the export.
      </p>
      <div style={{ border: '1.5px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
        <RichEditor itemId={personId} value={value} onChange={onChange} />
      </div>
    </div>
  );
}

function EmptyKundaliNote({ text }) {
  return (
    <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '12.5px', lineHeight: 1.6 }}>{text}</div>
  );
}

function ComingSoonSection({ label }) {
  return (
    <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
      <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '6px' }}>{label}</div>
      <p style={{ fontSize: '11.5px', lineHeight: 1.6, maxWidth: '360px', margin: '0 auto' }}>
        Coming soon — needs either more reference examples or the exact source calculation to build accurately,
        without guessing.
      </p>
    </div>
  );
}

// One report section, in its own bordered card with a heading — used to be a
// nav item you switched to on the right; now every section just renders one
// after another, so this only needs to know how to draw itself + its title.
function KundaliSectionCard({ title, noChrome, children }) {
  if (noChrome) return children; // the "Kundali" charts already draw their own bordered cards
  return (
    <div style={{ background: '#fff', borderRadius: '10px', border: '1.5px solid #e2e8f0', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {title}
      </div>
      <div style={{ padding: '16px' }}>{children}</div>
    </div>
  );
}

function KundaliReportsPanel({ astroData, personId, onSuggestionsChange, chartsContent, generateButton, language = 'en' }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {generateButton}
      <KundaliSectionCard title={kundaliTextLabel(KUNDALI_SECTIONS[0].label, language)} noChrome>{chartsContent}</KundaliSectionCard>
      <KundaliSectionCard title={kundaliTextLabel('Vimsottari Table', language)}>
        <VimsottariTableSection vimshottari={astroData.vimshottari} language={language} />
      </KundaliSectionCard>
      <KundaliSectionCard title={kundaliTextLabel('Navatara Chakra', language)}>
        <NavataraChakraSection moonNakshatraNumber={astroData.moonNakshatraNumber} language={language} />
      </KundaliSectionCard>
      <KundaliSectionCard title={kundaliTextLabel('Ruling Deities', language)}>
        <RulingDeitySection moonNakshatraNumber={astroData.moonNakshatraNumber} language={language} />
      </KundaliSectionCard>
      <KundaliSectionCard title={kundaliTextLabel('Astro-Vastu Plant Remedy', language)}>
        <PlantRemedySection moonNakshatraNumber={astroData.moonNakshatraNumber} language={language} />
      </KundaliSectionCard>
      <KundaliSectionCard title={kundaliTextLabel('General House Activity Mapping', language)}>
        <ActivityMappingSection ashtakavarga={astroData.ashtakavarga} lagnaRashi={astroData.lagnaRashi} mode="house" language={language} />
      </KundaliSectionCard>
      <KundaliSectionCard title={kundaliTextLabel('Office/Shop/Factory Layout Mapping', language)}>
        <ActivityMappingSection ashtakavarga={astroData.ashtakavarga} lagnaRashi={astroData.lagnaRashi} mode="office" language={language} />
      </KundaliSectionCard>
      <KundaliSectionCard title={kundaliTextLabel('Hit Theory', language)}>
        <HitTheorySection hitTheory={astroData.hitTheory} language={language} />
      </KundaliSectionCard>
      <KundaliSectionCard title={kundaliTextLabel('Best Direction Table', language)}>
        <BestDirectionSection moonNakshatraNumber={astroData.moonNakshatraNumber} language={language} />
      </KundaliSectionCard>
      <KundaliSectionCard title={kundaliTextLabel('General Suggestions & Remedies', language)}>
        <GeneralSuggestionsSection personId={personId} value={astroData.suggestionsHtml} onChange={onSuggestionsChange} />
      </KundaliSectionCard>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Main component
// ══════════════════════════════════════════════════════════════════════════════
export default function VastuPersonsGateway({
  openModal    = false,
  onModalClose = null,
  projectName  = null,
  projectId    = null,
  initialView  = 'persons',  // 'persons' | 'numero' | 'astro'
  onAstroSaved = null,       // fires when Astro Vastu data is saved — auto-enables the export toggle, same as every other report
  onNumeroSaved = null,      // fires when Numero Vastu (Lo Shu) data is saved — same combined-export toggle as Kua below
  onKuaSaved = null,         // fires when Kua Number data is saved — shares the "Numero Vastu Report" export toggle, so a
                             // Kua-only save (no Lo Shu persons yet) needs this too, or the toggle stays disabled and Kua
                             // Number Analysis never makes it into the exported PDF
  language = 'en',           // selected report language — threads into buildKuaHtml / buildAstroAnalysisHtml
}) {
  // Person/Numero/Kua/Astro data was previously stored under flat, un-scoped
  // localStorage keys — every one of a user's projects shared the exact same
  // entries, so switching projects showed the wrong project's people. Scoping
  // each key by projectId fixes that; the parent already remounts this whole
  // component on projectId change (see its `key` prop), so a fresh mount here
  // always starts from the right project's data.
  // Mobile: this gateway's header/toolbar/grids were desktop-only —
  // the header's full-text action buttons overflowed a phone header, the
  // person-form and Kua grids assumed desktop widths, and the Kundali
  // report's fixed 220px side-nav ate over half the screen. Fixed below
  // via isMobile-gated styles only; desktop keeps every original style
  // untouched.
  const isMobile = useIsMobile();
  const scopedPersonsKey = `${PERSONS_KEY}_${projectId || 'new'}`;
  const scopedNumeroKey  = `${NUMERO_KEY}_${projectId || 'new'}`;
  const scopedKuaKey     = `${KUA_KEY}_${projectId || 'new'}`;
  const scopedAstroKey   = `${ASTRO_KEY}_${projectId || 'new'}`;
  const scopedChartStyleKey = `${ASTRO_CHART_STYLE_KEY}_${projectId || 'new'}`;

  // 'persons' | 'numero' | 'astro'
  const [view,          setView]          = useState(initialView);
  const [persons,       setPersons]       = useState([newPerson()]);

  // Numero Vastu states
  const [isGenerating,  setIsGenerating]  = useState(false);
  const [kuaMode,       setKuaMode]       = useState(false);
  const [kuaItems,      setKuaItems]      = useState([]);
  const [kuaGenerating, setKuaGenerating] = useState(false);

  // Astro Vastu states
  const [astroData,     setAstroData]     = useState({}); // personId → { lagnaRashi, houses, richHtml }
  // Every person now gets their own full stacked page (see the view==='astro'
  // render below) instead of one shared "active person" tab — so which house
  // is selected in the D1 chart, previously single current-person-only
  // state, needs to be tracked PER PERSON now, or selecting a house on
  // Person 1's chart would also select it on Person 2's chart (every Kundali
  // report section itself now just renders one after another for everyone —
  // no per-person "active section" state needed any more).
  const [selHouseByPerson, setSelHouseByPerson] = useState({}); // personId → houseNum|null
  // Astro view: which member's card is currently in view (drives the name-chip bar highlight)
  const [activeAstroPersonId, setActiveAstroPersonId] = useState(null);
  const jumpToAstroPerson = (pid) => {
    setActiveAstroPersonId(pid);
    const el = document.getElementById(`astro-person-${pid}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  // Scroll-spy: highlight the chip of the card nearest the top of the scroll area.
  const handleAstroScroll = (e) => {
    const box = e.currentTarget.getBoundingClientRect();
    let current = null;
    for (const p of persons) {
      const el = document.getElementById(`astro-person-${p.id}`);
      if (!el) continue;
      if (el.getBoundingClientRect().top - box.top <= 90) current = p.id;
    }
    if (current && current !== activeAstroPersonId) setActiveAstroPersonId(current);
  };
  // North / South Indian kundli: the user picks the style(s) the report prints (at least one stays on).
  const [chartStyles, setChartStyles] = useState({ north: true, south: true });
  const toggleChartStyle = (style) => {
    setChartStyles(prev => {
      const next = { ...prev, [style]: !prev[style] };
      if (!next.north && !next.south) return prev;
      try { localStorage.setItem(scopedChartStyleKey, JSON.stringify(next)); } catch (e) {}
      return next;
    });
  };
  const getSelHouse   = (pid) => selHouseByPerson[pid] ?? null;
  const setSelHouseFor = (pid, val) => setSelHouseByPerson(prev => ({ ...prev, [pid]: val }));
  const [astroGenerating, setAstroGenerating] = useState(false);
  // Single-person "Generate Chart" is intentionally serialized (one person's
  // 3 API calls at a time, same rate-limit reasoning as calcAllAstroFromApi
  // below) — so this holds the ONE person id currently being calculated
  // (null when idle), and every other person's Generate button is disabled
  // while it's set, but only that one person's button shows the spinner.
  const [astroApiLoadingId, setAstroApiLoadingId] = useState(null);
  const astroApiLoading = astroApiLoadingId != null;
  const [astroApiError,   setAstroApiError]   = useState('');    // last API error, shown once above the person list

  // ── Restore on open ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!openModal) return;
    try {
      const raw = localStorage.getItem(scopedPersonsKey) || localStorage.getItem(scopedNumeroKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) setPersons(parsed);
        else setPersons([newPerson()]);
      } else {
        setPersons([newPerson()]);
      }
      const astroRaw = localStorage.getItem(scopedAstroKey);
      if (astroRaw) setAstroData(JSON.parse(astroRaw));
      const styleRaw = localStorage.getItem(scopedChartStyleKey);
      const st = styleRaw ? JSON.parse(styleRaw) : null;
      setChartStyles(st && (st.north || st.south) ? { north: !!st.north, south: !!st.south } : { north: true, south: true });
    } catch (e) {}
    setView(initialView);
    setKuaMode(false);
    setSelHouseByPerson({});
  }, [openModal, initialView, scopedPersonsKey, scopedNumeroKey, scopedAstroKey, scopedChartStyleKey]);

  const handleClose = () => {
    // Auto-save whatever is currently entered so closing the modal (from any
    // view, any button) never silently loses data — matches how the other
    // reports' data is always picked up at export/project-save time without
    // needing a dedicated manual save first.
    persistPersons(persons);
    if (onModalClose) onModalClose();
  };

  // ── Person handlers ───────────────────────────────────────────────────────
  const handlePersonChange = useCallback((id, field, val) => {
    setPersons(prev => prev.map(p => p.id === id ? { ...p, [field]: val } : p));
  }, []);
  const handlePersonAdd    = () => setPersons(prev => [...prev, newPerson()]);
  const handlePersonRemove = useCallback((id) => {
    setPersons(prev => prev.length > 1 ? prev.filter(p => p.id !== id) : prev);
  }, []);

  const persistPersons = useCallback((list) => {
    try {
      localStorage.setItem(scopedPersonsKey, JSON.stringify(list));
      localStorage.setItem(scopedNumeroKey,  JSON.stringify(list)); // backward compat
    } catch (e) {}
  }, [scopedPersonsKey, scopedNumeroKey]);

  // ── Go to Numero / Astro ──────────────────────────────────────────────────
  const goToNumero = () => {
    persistPersons(persons);
    setView('numero');
    setKuaMode(false);
  };

  const goToAstro = () => {
    persistPersons(persons);
    setAstroData(prev => {
      const next = { ...prev };
      persons.forEach(p => { if (!next[p.id]) next[p.id] = newAstroPersonData(); });
      return next;
    });
    setSelHouseByPerson({});
    setView('astro');
  };

  const goBack = () => { setView('persons'); setKuaMode(false); setSelHouseByPerson({}); };

  // ── Numero / Kua handlers ─────────────────────────────────────────────────
  const enterKuaMode = useCallback(() => {
    let savedMap = {};
    try {
      const raw = localStorage.getItem(scopedKuaKey);
      if (raw) JSON.parse(raw).forEach(k => { savedMap[k.id] = k; });
    } catch (e) {}
    setKuaItems(persons.map((p, i) => {
      const freshKua = calcKuaNumber(p.dob, p.gender);
      const saved = savedMap[p.id];
      // Only reuse the saved rich-text edit if it was generated for the same
      // Kua number AND the same content template version. Without the
      // version check, anyone who had already saved Kua data before the
      // Profile/Numerology/Direction content was corrected kept seeing their
      // OLD cached HTML forever — buildKuaHtml() changes never showed up
      // "niche" because the stale saved copy always won. Bumping
      // KUA_HTML_VERSION forces exactly one regeneration for old saves;
      // genuine edits made after that keep being respected as before.
      // Keep a saved edit only if the user actually hand-edited it; an
      // untouched default (in any language) is regenerated in the currently
      // selected language so the export follows the dropdown.
      const savedIsEdited = saved && saved.richHtml && saved._kuaHtmlVersion === KUA_HTML_VERSION && !isDefaultKuaHtml(p, saved.richHtml);
      const isFresh = savedIsEdited && saved.kuaNumber === freshKua;
      const richHtml = isFresh ? saved.richHtml : buildKuaHtml(p, language);
      return {
        id: p.id, personName: p.name || `Person ${i + 1}`,
        kuaNumber: freshKua,
        richHtml,
        _kuaHtmlVersion: KUA_HTML_VERSION,
      };
    }));
    setKuaMode(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persons, scopedKuaKey, language]);

  // Language switch while in Kua mode: re-translate every card still showing
  // an auto-built default (hand-edited cards are left untouched).
  useEffect(() => {
    setKuaItems(prev => {
      if (prev.length === 0) return prev;
      let changed = false;
      const next = prev.map(k => {
        const p = persons.find(pp => pp.id === k.id) || { name: k.personName };
        if (!isDefaultKuaHtml(p, k.richHtml)) return k;
        const translated = buildKuaHtml(p, language);
        if (translated === k.richHtml) return k;
        changed = true;
        return { ...k, richHtml: translated };
      });
      return changed ? next : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Language switch: re-translate every person's Astro "Analysis Notes" that
  // still shows the auto-built default (hand-edited notes are left
  // untouched) — same pattern as the Kua cards above.
  useEffect(() => {
    setAstroData(prev => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((pid) => {
        const ad = next[pid];
        if (!ad || !ad.lagnaRashi) return;
        if (!isDefaultAstroHtml(ad.lagnaRashi, ad.houses || {}, getRashi, ad.richHtml)) return;
        const translated = buildAstroAnalysisHtml(ad.lagnaRashi, ad.houses || {}, getRashi, language);
        if (translated === ad.richHtml) return;
        changed = true;
        next[pid] = { ...ad, richHtml: translated };
      });
      return changed ? next : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const handleKuaHtmlChange = useCallback((id, html) => {
    setKuaItems(prev => prev.map(k => k.id === id ? { ...k, richHtml: html, _kuaHtmlVersion: KUA_HTML_VERSION } : k));
  }, []);

  const saveNumeroData = useCallback(() => {
    try {
      persistPersons(persons);
      if (onNumeroSaved) onNumeroSaved();
      alert('Numero Vastu report saved!');
      handleClose();
    } catch (e) { alert('Error saving: ' + e.message); }
  }, [persons, persistPersons, onNumeroSaved]);

  const saveKuaData = useCallback(() => {
    try {
      localStorage.setItem(scopedKuaKey, JSON.stringify(kuaItems));
      if (onKuaSaved) onKuaSaved();
      alert('Kua Number data saved!');
    } catch (e) { alert('Error saving: ' + e.message); }
  }, [kuaItems, scopedKuaKey, onKuaSaved]);

  // ── Astro handlers ────────────────────────────────────────────────────────
  const getAD = useCallback((pid) => astroData[pid] || newAstroPersonData(), [astroData]);

  const setLagnaRashi = (pid, rashi) => {
    setAstroData(prev => ({ ...prev, [pid]: { ...getAD(pid), lagnaRashi: rashi } }));
    setSelHouseFor(pid, null);
  };

  // Core per-person computation — fetches D1 chart + Vimshottari +
  // Ashtakavarga + KP chart (for Hit Theory) and writes them into astroData,
  // with NO loading/error UI side effects of its own. Shared by the
  // single-person "Generate Chart" button below and the "Generate All"
  // batch action, each of which owns its own loading/error state instead
  // (a shared one would flicker between people in a loop, and "Generate
  // All" needs to keep going — and report — on a per-person basis even if
  // one person's calculation fails).
  const computeAstroForPerson = useCallback(async (person) => {
    const { lagnaRashi, houses, ascendantNormDegree, moonNakshatraNumber, resolvedPlace, latitude, longitude, timezone } =
      await computeD1Chart({ dob: person.dob, tob: person.tob, place: person.place, placeGeo: person.placeGeo });
    const calcMeta = { dob: person.dob, tob: person.tob, place: resolvedPlace, latitude, longitude, timezone };

    const [vimshottariResult, ashtakavargaResult, kpChartResult] = await Promise.allSettled([
      getVimshottariDasha({ dob: person.dob, tob: person.tob, place: person.place, placeGeo: person.placeGeo }),
      getAshtakavarga({ dob: person.dob, tob: person.tob, place: person.place, placeGeo: person.placeGeo }),
      getKpChart({ dob: person.dob, tob: person.tob, place: person.place, placeGeo: person.placeGeo }),
    ]);
    const vimshottari  = vimshottariResult.status === 'fulfilled' ? vimshottariResult.value : null;
    const ashtakavarga = ashtakavargaResult.status === 'fulfilled' ? ashtakavargaResult.value : null;
    // Hit Theory — the KP chart only gives raw longitudes; the two grids
    // (Planet↔Planet, Planet↔House) are computed here client-side against
    // KundaliReportsData.js's HIT_MASTER, same as every other Kundali
    // report section computes its own table from raw API data.
    let hitTheory = null;
    if (kpChartResult.status === 'fulfilled') {
      const { planets, cusps } = kpChartResult.value;
      hitTheory = { planets, cusps, planetToPlanet: generatePlanetPlanetGrid(planets), planetToHouse: generatePlanetHouseGrid(planets, cusps) };
    }

    setAstroData(prev => {
      const cur = getAD(person.id);
      // Only auto-draft Analysis Notes if the box is still empty — never
      // overwrite something the astrologer already typed.
      const isBlank = !cur.richHtml || cur.richHtml === '<p><br></p>' || cur.richHtml === '';
      const richHtml = isBlank ? buildAstroAnalysisHtml(lagnaRashi, houses, getRashi, language) : cur.richHtml;
      return { ...prev, [person.id]: { ...cur, lagnaRashi, houses, ascendantNormDegree, moonNakshatraNumber, vimshottari, ashtakavarga, hitTheory, richHtml, birthKey: birthKeyOf(person), calcMeta } };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAD, language]);

  // Auto-fills lagnaRashi + houses for ONE person via the real D1 API instead
  // of manual Lagna/Graha clicking — writes into the same astroData shape,
  // so D1Chart / PDF export downstream don't need to know the difference.
  const calcAstroFromApi = useCallback(async (person) => {
    if (!person) return;
    setAstroApiError('');
    setAstroApiLoadingId(person.id);
    try {
      await computeAstroForPerson(person);
      setSelHouseFor(person.id, null);
    } catch (e) {
      setAstroApiError(e.message || 'Could not calculate the Astro report.');
    } finally {
      setAstroApiLoadingId(null);
    }
  }, [computeAstroForPerson]);

  // "Generate All" — runs computeAstroForPerson for every person that has
  // enough birth details, one at a time (not in parallel — navamsha.in's
  // rate limit already needed a 429-retry for a single person's 3 calls;
  // running several people's calls at once would make that far more
  // likely). Keeps going even if one person fails, and reports which ones
  // did at the end instead of stopping the whole batch.
  const [astroGenerateAllLoading, setAstroGenerateAllLoading] = useState(false);
  const [astroGenerateAllProgress, setAstroGenerateAllProgress] = useState(null); // { done, total }

  const calcAllAstroFromApi = useCallback(async () => {
    const eligible = persons.filter(p => p.dob && p.tob && p.place);
    if (!eligible.length) {
      setAstroApiError('Add date of birth, time of birth, and place for at least one person first.');
      return;
    }
    setAstroApiError('');
    setAstroGenerateAllLoading(true);
    setAstroGenerateAllProgress({ done: 0, total: eligible.length });
    const failed = [];
    for (let i = 0; i < eligible.length; i++) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await computeAstroForPerson(eligible[i]);
      } catch (e) {
        failed.push(eligible[i].name || `Person ${i + 1}`);
      }
      setAstroGenerateAllProgress({ done: i + 1, total: eligible.length });
    }
    setAstroGenerateAllLoading(false);
    setAstroGenerateAllProgress(null);
    if (failed.length) {
      setAstroApiError(`Could not generate for: ${failed.join(', ')}. You can retry those individually from their own tab.`);
    }
  }, [persons, computeAstroForPerson]);

  // Auto-prompt to bulk-generate right when the Astro/Kundali view is
  // opened, instead of a permanent "Generate All" header button (removed
  // per client request). Resets each time the modal is freshly opened, so
  // dismissing it doesn't suppress it forever — only until the editor is
  // closed and reopened.
  const [showGenerateAllPrompt, setShowGenerateAllPrompt] = useState(false);
  const [generateAllPromptDismissed, setGenerateAllPromptDismissed] = useState(false);

  useEffect(() => {
    if (openModal) setGenerateAllPromptDismissed(false);
  }, [openModal]);

  useEffect(() => {
    if (!openModal || view !== 'astro' || generateAllPromptDismissed) {
      setShowGenerateAllPrompt(false);
      return;
    }
    const pending = persons.some(p => p.dob && p.tob && p.place && !getAD(p.id).lagnaRashi);
    setShowGenerateAllPrompt(pending);
  }, [openModal, view, generateAllPromptDismissed, persons, getAD]);

  const togglePlanet = (pid, houseNum, planet) => {
    setAstroData(prev => {
      const cur = getAD(pid);
      const curPlanets = [...(cur.houses?.[houseNum] || [])];
      const idx = curPlanets.indexOf(planet);
      if (idx >= 0) curPlanets.splice(idx, 1); else curPlanets.push(planet);
      return { ...prev, [pid]: { ...cur, houses: { ...cur.houses, [houseNum]: curPlanets } } };
    });
  };

  const handleAstroHtmlChange = useCallback((id, html) => {
    setAstroData(prev => ({ ...prev, [id]: { ...(prev[id] || newAstroPersonData()), richHtml: html } }));
  }, []);

  // General Suggestions / remedies — the reference report's own version of
  // this section is just empty "Remedy" placeholder bullets meant for the
  // astrologer to fill in by hand, not an auto-generated table, so this is
  // a free-text field (same RichEditor already used for Analysis Notes)
  // rather than a formula.
  const handleSuggestionsChange = useCallback((id, html) => {
    setAstroData(prev => ({ ...prev, [id]: { ...(prev[id] || newAstroPersonData()), suggestionsHtml: html } }));
  }, []);

  const saveAstroData = useCallback(() => {
    try {
      localStorage.setItem(scopedAstroKey, JSON.stringify(astroData));
      persistPersons(persons);
      if (onAstroSaved) onAstroSaved();
      handleClose();
    } catch (e) { alert('Error saving: ' + e.message); }
  }, [astroData, persons, persistPersons, scopedAstroKey, onAstroSaved]);

  // ── Numero PDF ────────────────────────────────────────────────────────────
  const downloadNumeroPDF = useCallback(async () => {
    const filled = persons.filter(p => p.name || p.dob);
    if (!filled.length) { alert('Please fill at least one person\'s Name or Date of Birth.'); return; }
    setIsGenerating(true);
    try {
      const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
      const { wrapAny, drawLineSmart } = createPdfTextHelpers(doc);
      const a4W = 210, a4H = 297;
      const genDate = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      const CELL_W = 54, CELL_H = 55, GAP = 3.5;
      const GRID_W = 3 * CELL_W + 2 * GAP;
      const GRID_X = (a4W - GRID_W) / 2;

      const getUserPoweredBy = () => {
        try {
          const u = JSON.parse(localStorage.getItem('user_data') || 'null');
          return (u && u.website_url && u.website_url.trim()) ? u.website_url.trim() : 'Powered by VastuAnalyzer.com';
        } catch { return 'Powered by VastuAnalyzer.com'; }
      };

      const addFrame = () => {
        // Ornate frame: accent border + light inner hairline + corner
        // brackets. Outer border sits exactly at the page corner (0,0) —
        // no gap between the page edge and the border any more.
        doc.setDrawColor(168, 94, 37); doc.setLineWidth(1);
        doc.rect(0, 0, a4W, a4H, 'S');
        doc.setDrawColor(224, 185, 148); doc.setLineWidth(0.2);
        doc.rect(2, 2, a4W - 4, a4H - 4, 'S');
        const armLen = 12;
        doc.setDrawColor(168, 94, 37); doc.setLineWidth(0.7);
        doc.line(0, 0, armLen, 0); doc.line(0, 0, 0, armLen);
        doc.line(a4W, 0, a4W - armLen, 0); doc.line(a4W, 0, a4W, armLen);
        doc.line(0, a4H, armLen, a4H); doc.line(0, a4H, 0, a4H - armLen);
        doc.line(a4W, a4H, a4W - armLen, a4H); doc.line(a4W, a4H, a4W, a4H - armLen);

        // Title — Title tier, theme accent colour. Subtitle (project name,
        // when present) — Subtitle tier, always black.
        const titleY = 11;
        drawLineSmart('Numero Vastu Report', a4W / 2, titleY, TYPE_TITLE_SIZE, 'bold', [124, 66, 28], 'center');
        let afterTitleY = titleY + 6;
        if (projectName) {
          const subtitleY = titleY + 9;
          drawLineSmart(projectName, a4W / 2, subtitleY, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK, 'center');
          afterTitleY = subtitleY + 8;
        }

        // Divider: line — dot — Om — dot — line (no full-width rule under it;
        // Om sized 2x, so it needs a little extra baseline offset to stay centred)
        const dividerY = afterTitleY;
        doc.setDrawColor(168, 94, 37); doc.setLineWidth(0.25);
        doc.line(a4W / 2 - 26, dividerY, a4W / 2 - 6, dividerY);
        doc.line(a4W / 2 + 6, dividerY, a4W / 2 + 26, dividerY);
        doc.setFillColor(168, 94, 37);
        doc.circle(a4W / 2 - 6, dividerY, 0.6, 'F');
        doc.circle(a4W / 2 + 6, dividerY, 0.6, 'F');
        drawLineSmart('ॐ', a4W / 2, dividerY + 2, 16, 'normal', [168, 94, 37], 'center');
      };

      const drawCell = (num, bx, by, isMissing) => {
        const d = LO_SHU_DATA[num];
        const [r, g, b] = d.colorRgb;
        doc.setFillColor(r, g, b);
        doc.roundedRect(bx, by, CELL_W, CELL_H, 3, 3, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24); doc.setFont('helvetica', 'bold');
        doc.text(String(num), bx + CELL_W / 2, by + 14, { align: 'center' });
        doc.setFontSize(7); doc.setFont('helvetica', 'bold');
        doc.text(`Direction: ${d.direction}`, bx + CELL_W / 2, by + 23, { align: 'center' });
        doc.setFontSize(6.5); doc.setFont('helvetica', 'normal');
        doc.text(`Planet: ${d.planet}`, bx + CELL_W / 2, by + 30, { align: 'center' });
        doc.text(`Element: ${d.element}`, bx + CELL_W / 2, by + 36, { align: 'center' });
        doc.setFontSize(6.5); doc.setFont('helvetica', 'bold');
        const useLines = doc.splitTextToSize(`Use: ${d.use}`, CELL_W - 8);
        useLines.forEach((line, i) => doc.text(line, bx + CELL_W / 2, by + 43 + i * 4, { align: 'center' }));
        if (isMissing) {
          // 50% opacity so the box's own number/text stays readable under the cross
          doc.saveGraphicsState();
          doc.setGState(new doc.GState({ opacity: 0.5, 'stroke-opacity': 0.5 }));
          doc.setDrawColor(215, 25, 25); doc.setLineWidth(2.5);
          doc.line(bx + 5, by + 5, bx + CELL_W - 5, by + CELL_H - 5);
          doc.line(bx + CELL_W - 5, by + 5, bx + 5, by + CELL_H - 5);
          doc.restoreGraphicsState();
          doc.setLineWidth(0.35);
        }
      };

      filled.forEach((person, pi) => {
        if (pi > 0) doc.addPage();
        addFrame();
        let curY = projectName ? 34 : 23;
        // Person's own name is this page's item-identity — Subtitle tier.
        drawLineSmart(person.name || `Person ${pi + 1}`, a4W / 2, curY + 3, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK, 'center');
        curY += 9;
        const detParts = [];
        if (person.gender) detParts.push(`Gender: ${person.gender}`);
        if (person.dob) { const dp = new Date(person.dob); detParts.push(`DOB: ${!isNaN(dp) ? dp.toLocaleDateString('en-GB') : person.dob}`); }
        if (person.place) detParts.push(`Place: ${person.place}`);
        if (detParts.length) {
          drawLineSmart(detParts.join('   ·   '), a4W / 2, curY, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK, 'center');
          curY += 8;
        }
        if (person.details) {
          const lines = wrapAny(person.details, TYPE_DESCRIPTION_SIZE, 'italic', a4W - 40);
          lines.forEach((l, i) => drawLineSmart(l, a4W / 2, curY + i * 7, TYPE_DESCRIPTION_SIZE, 'italic', TYPE_BLACK, 'center'));
          curY += lines.length * 7 + 1;
        }
        curY += 3;
        doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.2);
        doc.line(15, curY, a4W - 15, curY);
        curY += 8;

        const { present, missing } = calcLoShu(person.dob);
        LO_SHU_GRID.forEach((row, ri) => {
          row.forEach((num, ci) => {
            drawCell(num, GRID_X + ci * (CELL_W + GAP), curY + ri * (CELL_H + GAP), missing.includes(num));
          });
        });
        // Present/Missing summary — Description tier, always black. Value
        // column X is measured off the actual label width at this font —
        // a hardcoded offset from the old smaller font was overlapping.
        const summY = curY + 3 * CELL_H + 2 * GAP + 12;
        doc.setFontSize(TYPE_DESCRIPTION_SIZE); doc.setFont('helvetica', 'bold'); doc.setTextColor(...TYPE_BLACK);
        const nvSummValX = GRID_X + Math.max(doc.getTextWidth('Present Numbers:'), doc.getTextWidth('Missing Numbers:')) + 4;
        doc.text('Present Numbers:', GRID_X, summY);
        doc.setFont('helvetica', 'normal');
        doc.text(present.length ? present.join('  ,  ') : 'None', nvSummValX, summY);
        doc.setFont('helvetica', 'bold'); doc.text('Missing Numbers:', GRID_X, summY + 9);
        doc.setFont('helvetica', 'normal');
        doc.text(missing.length ? missing.join('  ,  ') : 'None', nvSummValX, summY + 9);
        doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(90, 53, 31);
        doc.text('May Vastu bring peace, happiness, prosperity always', a4W / 2, a4H - 15, { align: 'center' });
        doc.setDrawColor(168, 94, 37); doc.setLineWidth(0.25);
        doc.line(a4W / 2 - 20, a4H - 10, a4W / 2 - 3, a4H - 10);
        doc.line(a4W / 2 + 3, a4H - 10, a4W / 2 + 20, a4H - 10);
        doc.setFillColor(168, 94, 37); doc.circle(a4W / 2, a4H - 10, 0.8, 'F');
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(124, 66, 28);
        doc.text(`Page ${pi + 1}`, a4W / 2, a4H - 4, { align: 'center' });
      });

      if (filled.length > 0) {
        doc.addPage(); addFrame();
        const total = filled.length;
        let cy = projectName ? 34 : 23;
        doc.setDrawColor(168, 94, 37); doc.setLineWidth(0.4); doc.line(15, cy, a4W - 15, cy); cy += 8;
        const TAB_X = 12, ROW_H = 13, HDR_H = 11;
        const CW = { num: 12, dir: 30, pln: 20, elm: 22, use: 38, str: 64 };
        const TAB_W = Object.values(CW).reduce((s, v) => s + v, 0);
        doc.setFillColor(168, 94, 37); doc.rect(TAB_X, cy, TAB_W, HDR_H, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(7.5); doc.setFont('helvetica', 'bold');
        let hx = TAB_X;
        [['#', CW.num], ['Direction', CW.dir], ['Planet', CW.pln], ['Element', CW.elm], ['Use', CW.use], ['Available Strength %', CW.str]].forEach(([lbl, w]) => {
          doc.text(lbl, hx + w / 2, cy + 7.5, { align: 'center' }); hx += w;
        });
        cy += HDR_H;
        [1, 2, 3, 4, 5, 6, 7, 8, 9].forEach((num, idx) => {
          const d = LO_SHU_DATA[num];
          const presentCount = filled.filter(p => calcLoShu(p.dob).present.includes(num)).length;
          const strength = total > 0 ? (presentCount / total) * 100 : 0;
          const dotClr = strength >= 66 ? [22, 163, 74] : strength >= 33 ? [202, 138, 4] : [220, 38, 38];
          doc.setFillColor(...(idx % 2 === 0 ? [255, 255, 255] : [245, 250, 250]));
          doc.rect(TAB_X, cy, TAB_W, ROW_H, 'F');
          doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.15); doc.rect(TAB_X, cy, TAB_W, ROW_H, 'S');
          let rx = TAB_X;
          const midY = cy + ROW_H / 2, textY = midY + 1.5;
          const [r, g, b] = d.colorRgb;
          doc.setFillColor(r, g, b); doc.roundedRect(rx + 1.5, cy + 1.5, CW.num - 3, ROW_H - 3, 2, 2, 'F');
          doc.setTextColor(255, 255, 255); doc.setFontSize(9.5); doc.setFont('helvetica', 'bold');
          doc.text(String(num), rx + CW.num / 2, textY, { align: 'center' }); rx += CW.num;
          doc.setTextColor(30, 30, 30); doc.setFontSize(7.5); doc.setFont('helvetica', 'normal');
          doc.text(d.direction, rx + 2, textY); rx += CW.dir;
          doc.text(d.planet, rx + 2, textY); rx += CW.pln;
          doc.text(d.element, rx + 2, textY); rx += CW.elm;
          const useLines = doc.splitTextToSize(d.use, CW.use - 4);
          useLines.slice(0, 2).forEach((l, li) => doc.text(l, rx + 2, cy + 4 + li * 4.5)); rx += CW.use;
          const dotX = rx + 4, barX = dotX + 8, barW = 30, barH = 4, barY2 = midY - barH / 2, pctX = barX + barW + 3;
          doc.setFillColor(...dotClr); doc.roundedRect(dotX - 2.5, midY - 2.5, 5, 5, 2.5, 2.5, 'F');
          doc.setFillColor(215, 215, 215); doc.roundedRect(barX, barY2, barW, barH, 1.5, 1.5, 'F');
          if (strength > 0) { const fillW = Math.max(1, (strength / 100) * barW); doc.setFillColor(...dotClr); doc.roundedRect(barX, barY2, fillW, barH, 1.5, 1.5, 'F'); }
          doc.setTextColor(0, 0, 0); doc.setFontSize(8.5); doc.setFont('helvetica', 'bold');
          doc.text(`${strength.toFixed(1)}%`, pctX, textY); cy += ROW_H;
        });
        cy += 8;
        const allStrong  = [1, 2, 3, 4, 5, 6, 7, 8, 9].filter(n => filled.every(p => calcLoShu(p.dob).present.includes(n)));
        const allMissing = [1, 2, 3, 4, 5, 6, 7, 8, 9].filter(n => filled.every(p => calcLoShu(p.dob).missing.includes(n)));
        // Body text below the table — Description tier, always black.
        if (allStrong.length) {
          doc.setFontSize(TYPE_DESCRIPTION_SIZE); doc.setFont('helvetica', 'bold'); doc.setTextColor(...TYPE_BLACK);
          wrapAny(`Strong in all persons (100%): ${allStrong.map(n => `${n}–${LO_SHU_DATA[n].direction}`).join(', ')}`, TYPE_DESCRIPTION_SIZE, 'bold', a4W - 2 * TAB_X).forEach(l => { doc.text(l, TAB_X, cy); cy += 8; });
        }
        if (allMissing.length) {
          doc.setFontSize(TYPE_DESCRIPTION_SIZE); doc.setFont('helvetica', 'bold'); doc.setTextColor(...TYPE_BLACK);
          wrapAny(`Missing in all persons (0%): ${allMissing.map(n => `${n}–${LO_SHU_DATA[n].direction}`).join(', ')}`, TYPE_DESCRIPTION_SIZE, 'bold', a4W - 2 * TAB_X).forEach(l => { doc.text(l, TAB_X, cy); cy += 8; });
        }
        doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(90, 53, 31);
        doc.text('May Vastu bring peace, happiness, prosperity always', a4W / 2, a4H - 15, { align: 'center' });
        doc.setDrawColor(168, 94, 37); doc.setLineWidth(0.25);
        doc.line(a4W / 2 - 20, a4H - 10, a4W / 2 - 3, a4H - 10);
        doc.line(a4W / 2 + 3, a4H - 10, a4W / 2 + 20, a4H - 10);
        doc.setFillColor(168, 94, 37); doc.circle(a4W / 2, a4H - 10, 0.8, 'F');
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(124, 66, 28);
        doc.text(`Page ${filled.length + 1}`, a4W / 2, a4H - 4, { align: 'center' });
      }
      const fname = (projectName || 'Numero_Vastu').replace(/[^a-zA-Z0-9_ -]/g, '').trim().replace(/\s+/g, '_');
      doc.save(`${fname}_Numero_Vastu.pdf`);
    } catch (err) {
      console.error('Numero PDF error:', err); alert('Error generating PDF: ' + err.message);
    } finally { setIsGenerating(false); }
  }, [persons, projectName]);

  // ── Kua PDF ───────────────────────────────────────────────────────────────
  const downloadKuaPDF = useCallback(async () => {
    if (!kuaItems.length) return;
    setKuaGenerating(true);
    try {
      const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
      const { wrapAny, drawLineSmart } = createPdfTextHelpers(doc);
      const a4W = 210, a4H = 297;
      const genDate = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

      const getUserPoweredBy = () => {
        try {
          const u = JSON.parse(localStorage.getItem('user_data') || 'null');
          return (u && u.website_url && u.website_url.trim()) ? u.website_url.trim() : 'Powered by VastuAnalyzer.com';
        } catch { return 'Powered by VastuAnalyzer.com'; }
      };

      const addFrame = () => {
        // Ornate frame: accent border + light inner hairline + corner
        // brackets. Outer border sits exactly at the page corner (0,0) —
        // no gap between the page edge and the border any more.
        doc.setDrawColor(168, 94, 37); doc.setLineWidth(1);
        doc.rect(0, 0, a4W, a4H, 'S');
        doc.setDrawColor(224, 185, 148); doc.setLineWidth(0.2);
        doc.rect(2, 2, a4W - 4, a4H - 4, 'S');
        const armLen = 12;
        doc.setDrawColor(168, 94, 37); doc.setLineWidth(0.7);
        doc.line(0, 0, armLen, 0); doc.line(0, 0, 0, armLen);
        doc.line(a4W, 0, a4W - armLen, 0); doc.line(a4W, 0, a4W, armLen);
        doc.line(0, a4H, armLen, a4H); doc.line(0, a4H, 0, a4H - armLen);
        doc.line(a4W, a4H, a4W - armLen, a4H); doc.line(a4W, a4H, a4W, a4H - armLen);

        // Title — Title tier, theme accent colour. Subtitle (project name,
        // when present) — Subtitle tier, always black.
        const titleY = 11;
        drawLineSmart('Kua Number Analysis', a4W / 2, titleY, TYPE_TITLE_SIZE, 'bold', [124, 66, 28], 'center');
        let afterTitleY = titleY + 6;
        if (projectName) {
          const subtitleY = titleY + 9;
          drawLineSmart(projectName, a4W / 2, subtitleY, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK, 'center');
          afterTitleY = subtitleY + 8;
        }

        // Divider: line — dot — Om — dot — line (no full-width rule under it;
        // Om sized 2x, so it needs a little extra baseline offset to stay centred)
        const dividerY = afterTitleY;
        doc.setDrawColor(168, 94, 37); doc.setLineWidth(0.25);
        doc.line(a4W / 2 - 26, dividerY, a4W / 2 - 6, dividerY);
        doc.line(a4W / 2 + 6, dividerY, a4W / 2 + 26, dividerY);
        doc.setFillColor(168, 94, 37);
        doc.circle(a4W / 2 - 6, dividerY, 0.6, 'F');
        doc.circle(a4W / 2 + 6, dividerY, 0.6, 'F');
        drawLineSmart('ॐ', a4W / 2, dividerY + 2, 16, 'normal', [168, 94, 37], 'center');
      };

      const parseSections = (html) => {
        if (!html) return [];
        const parts = html.split(/<h2[^>]*>/i).filter(Boolean);
        return parts.slice(1).map(part => {
          const m = part.match(/^(.*?)<\/h2>([\s\S]*)/i);
          if (!m) return null;
          const heading = m[1].replace(/<[^>]+>/g, '').trim();
          const pTags = m[2].match(/<p[^>]*>([\s\S]*?)<\/p>/gi) || [];
          const lines = pTags.map(p => p.replace(/<strong>/gi,'').replace(/<\/strong>/gi,'').replace(/<[^>]+>/g,'').replace(/&amp;/g,'&').replace(/&nbsp;/g,' ').replace(/\s+/g,' ').trim()).filter(Boolean);
          if (!lines.length) { const t = m[2].replace(/<[^>]+>/g,'').replace(/&nbsp;/g,' ').replace(/\s+/g,' ').trim(); if (t) lines.push(t); }
          return heading || lines.length ? { heading, lines } : null;
        }).filter(Boolean);
      };

      // Description-tier (16pt) line height, scaled up from the old 8.5pt
      // body's 5.5mm the same way every other report's LINE_H was rescaled
      // when its font size grew.
      const LINE_H = 8.5, CX = 12, CW2 = a4W - 24;
      const PAGE_TOP = projectName ? 34 : 23, PAGE_BOT = a4H - 22;
      const ensureSpace = (cy, need) => { if (cy + need > PAGE_BOT) { doc.addPage(); addFrame(); return PAGE_TOP; } return cy; };

      kuaItems.forEach((item, pi) => {
        if (pi > 0) doc.addPage();
        addFrame();
        let cy = PAGE_TOP;
        const kd = item.kuaNumber ? KUA_DATA[item.kuaNumber] : null;
        // Person's own name is this page's item-identity — Subtitle tier.
        drawLineSmart(item.personName, CX, cy + 8, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK);
        if (kd) { drawLineSmart(`${kuaLabel('kuaNumber', language)} ${item.kuaNumber}  ·  ${kuaGroup(kd.group, language)} ${kuaLabel('group', language)}  ·  ${kuaElement(kd.element, language)}  ·  ${kuaPersonality(item.kuaNumber, language)}`, CX, cy + 18, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK); }
        cy += 26;
        doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.4); doc.line(CX, cy, a4W - CX, cy); cy += 6;
        const sections = parseSections(item.richHtml || '');
        sections.forEach(sec => {
          cy = ensureSpace(cy, 20);
          // Section heading — Description tier, bold, black.
          drawLineSmart(sec.heading.toUpperCase(), CX, cy + LINE_H, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK); cy += LINE_H + 1;
          doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.35); doc.line(CX, cy, CX + CW2, cy); cy += 4;
          sec.lines.forEach(line => {
            cy = ensureSpace(cy, LINE_H + 1);
            const ci = line.indexOf(':');
            if (ci > 0) {
              const label = line.substring(0, ci).trim(), value = line.substring(ci + 1).trim();
              const lblStr = label + ':  ';
              const lblIsHindi = DEVANAGARI_RE.test(lblStr);
              doc.setFontSize(TYPE_DESCRIPTION_SIZE); doc.setFont('helvetica', 'bold');
              const lblW = lblIsHindi
                ? (() => { hindiMeasureCtx.font = hindiFontCss(TYPE_DESCRIPTION_SIZE, 'bold'); return hindiMeasureCtx.measureText(lblStr).width / PX_PER_MM; })()
                : doc.getTextWidth(lblStr);
              drawLineSmart(lblStr, CX + 4, cy + LINE_H, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
              const valLines = wrapAny(value, TYPE_DESCRIPTION_SIZE, 'normal', CW2 - 8 - lblW);
              drawLineSmart(valLines[0], CX + 4 + lblW, cy + LINE_H, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK); cy += LINE_H;
              for (let i = 1; i < valLines.length; i++) { cy = ensureSpace(cy, LINE_H); drawLineSmart(valLines[i], CX + 4 + lblW + 2, cy + LINE_H, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK); cy += LINE_H; }
              cy += 1;
            } else {
              const ls = wrapAny(line, TYPE_DESCRIPTION_SIZE, 'normal', CW2 - 8);
              ls.forEach(l => { cy = ensureSpace(cy, LINE_H); drawLineSmart(l, CX + 4, cy + LINE_H, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK); cy += LINE_H; }); cy += 1;
            }
          });
          cy += 5;
        });
        doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(90, 53, 31);
        doc.text('May Vastu bring peace, happiness, prosperity always', a4W / 2, a4H - 15, { align: 'center' });
        doc.setDrawColor(168, 94, 37); doc.setLineWidth(0.25);
        doc.line(a4W / 2 - 20, a4H - 10, a4W / 2 - 3, a4H - 10);
        doc.line(a4W / 2 + 3, a4H - 10, a4W / 2 + 20, a4H - 10);
        doc.setFillColor(168, 94, 37); doc.circle(a4W / 2, a4H - 10, 0.8, 'F');
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(124, 66, 28);
        doc.text(`Page ${pi + 1}`, a4W / 2, a4H - 4, { align: 'center' });
      });
      const fname = (projectName || 'Kua_Number').replace(/[^a-zA-Z0-9_ -]/g, '').trim().replace(/\s+/g, '_');
      doc.save(`${fname}_Kua_Number.pdf`);
    } catch (err) { console.error('Kua PDF error:', err); alert('Error: ' + err.message); }
    finally { setKuaGenerating(false); }
  }, [kuaItems, projectName]);

  // ── Astro PDF ─────────────────────────────────────────────────────────────
  const downloadAstroPDF = useCallback(async () => {
    const filled = persons.filter(p => p.name || p.dob);
    if (!filled.length) { alert('Please fill at least one person\'s details.'); return; }
    setAstroGenerating(true);
    try {
      const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
      const { drawLineSmart, wrapAny } = createPdfTextHelpers(doc);
      const a4W = 210, a4H = 297;
      const genDate = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

      // Same section parser used by the Kua PDF below — Analysis Notes richHtml
      // uses the identical <h2>/<p><strong>Label:</strong> value</p> shape.
      const parseAstroNoteSections = (html) => {
        if (!html) return [];
        const parts = html.split(/<h2[^>]*>/i).filter(Boolean);
        return parts.slice(1).map(part => {
          const m = part.match(/^(.*?)<\/h2>([\s\S]*)/i);
          if (!m) return null;
          const heading = m[1].replace(/<[^>]+>/g, '').trim();
          const pTags = m[2].match(/<p[^>]*>([\s\S]*?)<\/p>/gi) || [];
          const lines = pTags.map(p => p.replace(/<strong>/gi, '').replace(/<\/strong>/gi, '').replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()).filter(Boolean);
          if (!lines.length) { const t = m[2].replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim(); if (t) lines.push(t); }
          return heading || lines.length ? { heading, lines } : null;
        }).filter(Boolean);
      };

      const getUserPoweredBy = () => {
        try {
          const u = JSON.parse(localStorage.getItem('user_data') || 'null');
          return (u && u.website_url && u.website_url.trim()) ? u.website_url.trim() : 'Powered by VastuAnalyzer.com';
        } catch { return 'Powered by VastuAnalyzer.com'; }
      };

      const addFrame = () => {
        // Ornate frame: accent border + light inner hairline + corner
        // brackets. Outer border sits exactly at the page corner (0,0) —
        // no gap between the page edge and the border any more.
        doc.setDrawColor(168, 94, 37); doc.setLineWidth(1);
        doc.rect(0, 0, a4W, a4H, 'S');
        doc.setDrawColor(224, 185, 148); doc.setLineWidth(0.2);
        doc.rect(2, 2, a4W - 4, a4H - 4, 'S');
        const armLen = 12;
        doc.setDrawColor(168, 94, 37); doc.setLineWidth(0.7);
        doc.line(0, 0, armLen, 0); doc.line(0, 0, 0, armLen);
        doc.line(a4W, 0, a4W - armLen, 0); doc.line(a4W, 0, a4W, armLen);
        doc.line(0, a4H, armLen, a4H); doc.line(0, a4H, 0, a4H - armLen);
        doc.line(a4W, a4H, a4W - armLen, a4H); doc.line(a4W, a4H, a4W, a4H - armLen);

        // Title — Title tier, theme accent colour. Subtitle (project name,
        // when present) — Subtitle tier, always black.
        const titleY = 11;
        drawLineSmart('Astro Report', a4W / 2, titleY, TYPE_TITLE_SIZE, 'bold', [124, 66, 28], 'center');
        let afterTitleY = titleY + 6;
        if (projectName) {
          const subtitleY = titleY + 9;
          drawLineSmart(projectName, a4W / 2, subtitleY, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK, 'center');
          afterTitleY = subtitleY + 8;
        }

        // Divider: line — dot — Om — dot — line (no full-width rule under it;
        // Om sized 2x, so it needs a little extra baseline offset to stay centred)
        const dividerY = afterTitleY;
        doc.setDrawColor(168, 94, 37); doc.setLineWidth(0.25);
        doc.line(a4W / 2 - 26, dividerY, a4W / 2 - 6, dividerY);
        doc.line(a4W / 2 + 6, dividerY, a4W / 2 + 26, dividerY);
        doc.setFillColor(168, 94, 37);
        doc.circle(a4W / 2 - 6, dividerY, 0.6, 'F');
        doc.circle(a4W / 2 + 6, dividerY, 0.6, 'F');
        drawLineSmart('ॐ', a4W / 2, dividerY + 2, 16, 'normal', [168, 94, 37], 'center');
      };

      filled.forEach((person, pi) => {
        if (pi > 0) doc.addPage();
        addFrame();
        const ad = astroData[person.id] || newAstroPersonData();
        let cy = projectName ? 34 : 23;

        // Same fixed direction/wall vocabulary translated for the PDF as for
        // the live D1Chart (see D1Chart's own comment) — the actual
        // Ascendant/planet placements still come straight from the API
        // untranslated, only the labels drawn around them change. Shared by
        // both the North Indian and South Indian chart blocks below.
        const translatePdfLabels = (houseLabels, translator) => {
          if (!houseLabels) return null;
          const out = {};
          Object.keys(houseLabels).forEach((h) => { out[h] = translator(houseLabels[h], language); });
          return out;
        };

        // Person's own name is this page's item-identity — Subtitle tier.
        drawLineSmart(person.name || `Person ${pi + 1}`, a4W / 2, cy + 3, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK, 'center'); cy += 9;

        const parts = [];
        if (person.gender) parts.push(`Gender: ${person.gender}`);
        if (person.dob) { const dp = new Date(person.dob); parts.push(`DOB: ${!isNaN(dp) ? dp.toLocaleDateString('en-GB') : person.dob}`); }
        if (person.place) parts.push(`Place: ${person.place}`);
        if (parts.length) {
          drawLineSmart(parts.join('   ·   '), a4W / 2, cy, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK, 'center'); cy += 8;
        }
        cy += 3; doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.2); doc.line(15, cy, a4W - 15, cy); cy += 5;

        if (ad.lagnaRashi) {
          const lr = RASHIS[ad.lagnaRashi - 1];
          // drawLineSmart, not raw doc.text — rashiLabel can now return a
          // non-Latin script name once a non-English report language is
          // selected, and jsPDF's built-in font renders those as mojibake.
          drawLineSmart(`Lagna (Ascendant): ${lr.name} (${rashiLabel(lr.en, language)})`, 12, cy, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK); cy += 8;

          // Moon Sign (Rasi) + birth Nakshatra, and the birth details the chart was calculated for —
          // the same two lines the live Astro editor shows above the chart.
          const moonHousePdf = Object.keys(ad.houses || {}).find((h) => (ad.houses[h] || []).includes('Moon'));
          const moonRashiPdf = moonHousePdf ? getRashi(ad.lagnaRashi, Number(moonHousePdf)) : null;
          if (moonRashiPdf) {
            const nakPdf = ad.moonNakshatraNumber ? getNakshatra(ad.moonNakshatraNumber) : null;
            drawLineSmart(`Moon Sign (Rasi): ${moonRashiPdf.name} (${rashiLabel(moonRashiPdf.en, language)})${nakPdf ? `   ·   Nakshatra: ${nakPdf.name}` : ''}`, 12, cy, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK); cy += 7;
          }
          if (ad.calcMeta) {
            const cm = ad.calcMeta;
            const tzPdf = Number(cm.timezone);
            drawLineSmart(`Calculated for: ${cm.dob} · ${cm.tob} · ${String(cm.place || '').split(',').slice(0, 3).join(',')} (${Number(cm.latitude).toFixed(2)}°, ${Number(cm.longitude).toFixed(2)}° · UTC${tzPdf >= 0 ? '+' : ''}${cm.timezone})`, 12, cy, 7.5, 'normal', [100, 116, 139]); cy += 6;
          }
        }

        // 4 charts, 2×2 — D1 plus its 3 relabeled variants (same geometry,
        // same computed Ascendant/planets; only the per-house label differs
        // — see VastuChartVariants.js).
        if (chartStyles.north) {
          const MINI = 85, GAP = 10;
          const gridW = MINI * 2 + GAP, gridX = (a4W - gridW) / 2;
          const astroVastuLabels = computeAstroVastuDirections(ad.ascendantNormDegree || 0);
          // Text size bumped to 1.5x current, matching the live-UI D1Chart
          // fix above (was left at its original size here; explicit
          // follow-up request to make the PDF export match too).
          const chartVariants = [
            { title: 'Chart D1',                 houseLabels: null,                                                       fontSize: 8.25 },
            { title: 'Vedic Vastu Chart',         houseLabels: translatePdfLabels(VEDIC_VASTU_DIRECTIONS, compassCodeLabel), fontSize: 7.5 },
            { title: 'AstroVastu Chart',          houseLabels: translatePdfLabels(astroVastuLabels, compassCodeLabel),      fontSize: 6.5 },
            { title: 'Laal Kitaab Makaan Chart',  houseLabels: translatePdfLabels(LAAL_KITAAB_DIRECTIONS, laalKitaabLabel), fontSize: 6.5 },
          ];
          chartVariants.forEach((v, i) => {
            const cx = gridX + (i % 2) * (MINI + GAP);
            const cyOff = cy + Math.floor(i / 2) * (MINI + 10);
            drawNorthIndianChartPdf(doc, { x: cx, y: cyOff, size: MINI, lagnaRashi: ad.lagnaRashi, houses: ad.houses || {}, getRashi, houseLabels: v.houseLabels, labelFontSize: v.fontSize, drawText: drawLineSmart });
            // Chart caption — bumped bigger + bold, was unreadably small.
            doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(30, 30, 30);
            doc.text(v.title, cx + MINI / 2, cyOff + MINI + 6, { align: 'center' });
          });
          cy += (MINI + 10) * 2 + 4;
        }

        // Compact reference table below the chart — same data, easy to scan/print.
        const TW = a4W - 24, TX = 12, RH = 7.2;
        const COL = [TW * 0.08, TW * 0.14, TW * 0.28, TW * 0.50];
        doc.setFillColor(0, 0, 0); doc.rect(TX, cy, TW, 7, 'F');
        doc.setTextColor(255, 255, 255); doc.setFontSize(7); doc.setFont('helvetica', 'bold');
        let hx = TX;
        ['#', 'House', 'Rashi', 'Planets / Grahas'].forEach((h, i) => {
          doc.text(h, hx + COL[i] / 2, cy + 5, { align: 'center' }); hx += COL[i];
        });
        cy += 7;

        HOUSES.forEach((h, i) => {
          const rashi = getRashi(ad.lagnaRashi, h.num);
          const planets = ad.houses?.[h.num] || [];
          doc.setFillColor(...(i % 2 === 0 ? [255, 255, 255] : [248, 250, 252]));
          doc.rect(TX, cy, TW, RH, 'F');
          doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.1); doc.rect(TX, cy, TW, RH, 'S');
          let rx = TX;
          const textY = cy + RH / 2 + 1.3;

          doc.setTextColor(168, 94, 37); doc.setFontSize(7.5); doc.setFont('helvetica', 'bold');
          doc.text(String(h.num), rx + COL[0] / 2, textY, { align: 'center' }); rx += COL[0];
          doc.setTextColor(30, 30, 30); doc.setFont('helvetica', 'bold');
          doc.text(h.name, rx + 2, textY); rx += COL[1];
          // drawLineSmart here too — same reason as the Lagna line above.
          if (rashi) drawLineSmart(`${rashi.name} (${rashiLabel(rashi.en, language)})`, rx + 2, textY, 7.5, 'normal', [80, 30, 150]);
          else drawLineSmart('—', rx + 2, textY, 7.5, 'normal', [80, 30, 150]);
          rx += COL[2];
          doc.setTextColor(30, 30, 30);
          const pStr = planets.join(', ') || '—';
          const pLines = doc.splitTextToSize(pStr, COL[3] - 4);
          doc.text(pLines[0], rx + 2, textY);
          cy += RH;
        });

        doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(90, 53, 31);
        doc.text('May Vastu bring peace, happiness, prosperity always', a4W / 2, a4H - 15, { align: 'center' });
        doc.setDrawColor(168, 94, 37); doc.setLineWidth(0.25);
        doc.line(a4W / 2 - 20, a4H - 10, a4W / 2 - 3, a4H - 10);
        doc.line(a4W / 2 + 3, a4H - 10, a4W / 2 + 20, a4H - 10);
        doc.setFillColor(168, 94, 37); doc.circle(a4W / 2, a4H - 10, 0.8, 'F');
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(124, 66, 28);
        doc.text(`Page ${pi + 1}`, a4W / 2, a4H - 4, { align: 'center' });

        // Same 4 charts again, on their own page, drawn South Indian style —
        // fixed Rashi cells with the house numbers moving around the
        // Ascendant instead (see drawSouthIndianChartPdf's own comment for
        // why that's the opposite convention from the North Indian chart
        // above). Matches the live-editor D1Chart, which shows both styles
        // stacked the same way.
        if (chartStyles.south) {
          doc.addPage();
          addFrame();
          let scy = projectName ? 34 : 23;
          drawLineSmart(`${person.name || `Person ${pi + 1}`} — South Indian Chart`, a4W / 2, scy + 3, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK, 'center');
          scy += 15;

          const SMINI = 85, SGAP = 10;
          const sGridW = SMINI * 2 + SGAP, sGridX = (a4W - sGridW) / 2;
          const sAstroVastuLabels = computeAstroVastuDirections(ad.ascendantNormDegree || 0);
          const sChartVariants = [
            { title: 'Chart D1',                 houseLabels: null,                                                        fontSize: 8.25 },
            { title: 'Vedic Vastu Chart',         houseLabels: translatePdfLabels(VEDIC_VASTU_DIRECTIONS, compassCodeLabel), fontSize: 7.5 },
            { title: 'AstroVastu Chart',          houseLabels: translatePdfLabels(sAstroVastuLabels, compassCodeLabel),     fontSize: 6.5 },
            { title: 'Laal Kitaab Makaan Chart',  houseLabels: translatePdfLabels(LAAL_KITAAB_DIRECTIONS, laalKitaabLabel), fontSize: 6.5 },
          ];
          sChartVariants.forEach((v, i) => {
            const cx = sGridX + (i % 2) * (SMINI + SGAP);
            const cyOff = scy + Math.floor(i / 2) * (SMINI + 10);
            drawSouthIndianChartPdf(doc, { x: cx, y: cyOff, size: SMINI, lagnaRashi: ad.lagnaRashi, houses: ad.houses || {}, houseLabels: v.houseLabels, labelFontSize: v.fontSize, drawText: drawLineSmart });
            doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(30, 30, 30);
            doc.text(v.title, cx + SMINI / 2, cyOff + SMINI + 6, { align: 'center' });
          });

          doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(90, 53, 31);
          doc.text('May Vastu bring peace, happiness, prosperity always', a4W / 2, a4H - 15, { align: 'center' });
          doc.setDrawColor(168, 94, 37); doc.setLineWidth(0.25);
          doc.line(a4W / 2 - 20, a4H - 10, a4W / 2 - 3, a4H - 10);
          doc.line(a4W / 2 + 3, a4H - 10, a4W / 2 + 20, a4H - 10);
          doc.setFillColor(168, 94, 37); doc.circle(a4W / 2, a4H - 10, 0.8, 'F');
          doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(124, 66, 28);
          doc.text(`Page ${pi + 1} (south indian chart)`, a4W / 2, a4H - 4, { align: 'center' });
        }

        // Analysis Notes — was missing from this export entirely; the chart
        // and reference table drew fine but the actual written interpretation
        // (auto-drafted or hand-edited) never made it into the PDF.
        const noteSections = parseAstroNoteSections(ad.richHtml || '');
        if (noteSections.length) {
          const AN_LINE_H = 8.5, AN_CX = 12, AN_CW = a4W - 24;
          const AN_PAGE_BOT = a4H - 22;
          doc.addPage();
          addFrame();
          let ncy = projectName ? 34 : 23;
          // This page's own item-identity line — Subtitle tier.
          drawLineSmart(`${person.name || `Person ${pi + 1}`} — ${kundaliTextLabel('Analysis Notes', language)}`, a4W / 2, ncy + 3, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK, 'center');
          ncy += 15;

          noteSections.forEach(sec => {
            if (ncy + 20 > AN_PAGE_BOT) { doc.addPage(); addFrame(); ncy = projectName ? 34 : 23; }
            drawLineSmart(sec.heading.toUpperCase(), AN_CX, ncy + AN_LINE_H, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
            ncy += AN_LINE_H + 1;
            doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.35);
            doc.line(AN_CX, ncy, AN_CX + AN_CW, ncy);
            ncy += 4;

            sec.lines.forEach(line => {
              if (ncy + AN_LINE_H + 1 > AN_PAGE_BOT) { doc.addPage(); addFrame(); ncy = projectName ? 34 : 23; }
              const ci = line.indexOf(':');
              if (ci > 0) {
                const label = line.substring(0, ci).trim(), value = line.substring(ci + 1).trim();
                const lblStr = label + ':  ';
                const lblIsHindi = DEVANAGARI_RE.test(lblStr);
                doc.setFontSize(TYPE_DESCRIPTION_SIZE); doc.setFont('helvetica', 'bold');
                const lblW = lblIsHindi
                  ? (() => { hindiMeasureCtx.font = hindiFontCss(TYPE_DESCRIPTION_SIZE, 'bold'); return hindiMeasureCtx.measureText(lblStr).width / PX_PER_MM; })()
                  : doc.getTextWidth(lblStr);
                drawLineSmart(lblStr, AN_CX + 4, ncy + AN_LINE_H, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
                const valLines = wrapAny(value, TYPE_DESCRIPTION_SIZE, 'normal', AN_CW - 8 - lblW);
                drawLineSmart(valLines[0], AN_CX + 4 + lblW, ncy + AN_LINE_H, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
                ncy += AN_LINE_H;
                for (let i = 1; i < valLines.length; i++) {
                  if (ncy + AN_LINE_H > AN_PAGE_BOT) { doc.addPage(); addFrame(); ncy = projectName ? 34 : 23; }
                  drawLineSmart(valLines[i], AN_CX + 4 + lblW + 2, ncy + AN_LINE_H, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
                  ncy += AN_LINE_H;
                }
                ncy += 1;
              } else {
                const ls = wrapAny(line, TYPE_DESCRIPTION_SIZE, 'normal', AN_CW - 8);
                ls.forEach(l => {
                  if (ncy + AN_LINE_H > AN_PAGE_BOT) { doc.addPage(); addFrame(); ncy = projectName ? 34 : 23; }
                  drawLineSmart(l, AN_CX + 4, ncy + AN_LINE_H, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
                  ncy += AN_LINE_H;
                });
                ncy += 1;
              }
            });
            ncy += 5;
          });

          doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(90, 53, 31);
          doc.text('May Vastu bring peace, happiness, prosperity always', a4W / 2, a4H - 15, { align: 'center' });
          doc.setDrawColor(168, 94, 37); doc.setLineWidth(0.25);
          doc.line(a4W / 2 - 20, a4H - 10, a4W / 2 - 3, a4H - 10);
          doc.line(a4W / 2 + 3, a4H - 10, a4W / 2 + 20, a4H - 10);
          doc.setFillColor(168, 94, 37); doc.circle(a4W / 2, a4H - 10, 0.8, 'F');
          doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(124, 66, 28);
          doc.text(`Page ${pi + 1} (notes)`, a4W / 2, a4H - 4, { align: 'center' });
        }

        // Kundali report sections — Vimsottari Table, Navatara Chakra,
        // House/Office Activity Mapping, General Suggestions. Were missing
        // from this export entirely (only the 4 charts + house/rashi table
        // + Analysis Notes made it in) — each gets its own page, same
        // black-header-row table style as the reference table above.
        const drawKundaliFooter = (label) => {
          doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(90, 53, 31);
          doc.text('May Vastu bring peace, happiness, prosperity always', a4W / 2, a4H - 15, { align: 'center' });
          doc.setDrawColor(168, 94, 37); doc.setLineWidth(0.25);
          doc.line(a4W / 2 - 20, a4H - 10, a4W / 2 - 3, a4H - 10);
          doc.line(a4W / 2 + 3, a4H - 10, a4W / 2 + 20, a4H - 10);
          doc.setFillColor(168, 94, 37); doc.circle(a4W / 2, a4H - 10, 0.8, 'F');
          doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(124, 66, 28);
          doc.text(`Page ${pi + 1} (${label})`, a4W / 2, a4H - 4, { align: 'center' });
        };

        // Draws one OR several tables on a shared page. These Kundali tables
        // are short (<= ~10 rows each), so a page per table left most of it
        // blank - related tables are merged onto one page and only spill to
        // a continuation page (header row repeated) if they truly don't fit.
        const drawKundaliTables = (tables, pageTitle) => {
          const pgTitle = pageTitle || tables.map(t => t.title).join(' & ');
          const ROW_PAGE_BOT = a4H - 22;
          const PAGE_TOP = projectName ? 34 : 23;
          const personLabel = person.name || `Person ${pi + 1}`;
          doc.addPage(); addFrame();
          let ty = PAGE_TOP;
          tables.forEach((t, ti) => {
            const { title, headers, colWidths, rows } = t;
            const tableW = colWidths.reduce((a, b) => a + b, 0);
            const TX = (a4W - tableW) / 2;
            if (ti > 0) ty += 18; // gap between tables sharing a page
            if (ty + 14 + 7 + 7 > ROW_PAGE_BOT) { drawKundaliFooter(pgTitle); doc.addPage(); addFrame(); ty = PAGE_TOP; }
            // This table's own item-identity line - Subtitle tier.
            drawLineSmart(`${personLabel} — ${title}`, 14, ty + 4, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK, 'left');
            ty += 14;
            const drawHeaderRow = () => {
              doc.setFillColor(0, 0, 0); doc.rect(TX, ty, tableW, 7, 'F');
              let hx = TX;
              headers.forEach((h, i) => { drawLineSmart(h, hx + colWidths[i] / 2, ty + 5, 7, 'bold', [255, 255, 255], 'center'); hx += colWidths[i]; });
              ty += 7;
            };
            drawHeaderRow();
            rows.forEach((row, ri) => {
              if (ty + 7 > ROW_PAGE_BOT) { drawKundaliFooter(pgTitle); doc.addPage(); addFrame(); ty = PAGE_TOP; drawHeaderRow(); }
              // A table can name one row to highlight (e.g. the remedy planet recommended for this person).
              const isHiRow = t.highlightRow === ri;
              doc.setFillColor(...(isHiRow ? [248, 236, 224] : ri % 2 === 0 ? [255, 255, 255] : [248, 250, 252]));
              doc.rect(TX, ty, tableW, 7, 'F');
              if (isHiRow) { doc.setDrawColor(168, 94, 37); doc.setLineWidth(0.4); } else { doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.1); }
              doc.rect(TX, ty, tableW, 7, 'S');
              let rx = TX;
              row.forEach((cell, ci) => {
                // A cell can be { text, bg:[r,g,b] } — Hit Theory cells with a hit get that
                // hit's colour (same as its code on screen) as a 70%-opacity background,
                // drawn as a blend with white.
                const hasBg = cell && typeof cell === 'object' && Array.isArray(cell.bg);
                const cellVal = hasBg ? cell.text : cell;
                if (hasBg) {
                  const blend = cell.bg.map(c => Math.round(255 * 0.3 + c * 0.7));
                  doc.setFillColor(...blend);
                  doc.rect(rx, ty, colWidths[ci], 7, 'F');
                  doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.1);
                  doc.rect(rx, ty, colWidths[ci], 7, 'S');
                }
                const cellStyle = (ci === 0 || hasBg || isHiRow) ? 'bold' : 'normal';
                const lines = wrapAny(String(cellVal ?? ''), 9.1, cellStyle, colWidths[ci] - 4);
                drawLineSmart(lines[0] || '', rx + 2, ty + 4.7, 9.1, cellStyle, hasBg ? [255, 255, 255] : isHiRow ? [124, 66, 28] : [30, 30, 30]);
                rx += colWidths[ci];
              });
              ty += 7;
            });
          });
          drawKundaliFooter(pgTitle);
        };

        // Page 1: Vimsottari Table + Navatara Chakra together.
        const dashaAndNavatara = [];
        if (ad.vimshottari?.mahadashas?.length) {
          dashaAndNavatara.push({ title: kundaliTextLabel('Vimsottari Table', language), headers: [kundaliTextLabel('Planet', language), kundaliTextLabel('Start', language), kundaliTextLabel('End', language)], colWidths: [40, 70, 70],
            rows: ad.vimshottari.mahadashas.map(md => [planetLabel(md.lord, language), formatDashaDate(md.start), formatDashaDate(md.end)]) });
        }
        if (ad.moonNakshatraNumber) {
          dashaAndNavatara.push({ title: kundaliTextLabel('Navatara Chakra', language), headers: [kundaliTextLabel('S.No.', language), kundaliTextLabel('Navatara', language), kundaliTextLabel('Lord', language), kundaliTextLabel('Nakshatra 1', language), kundaliTextLabel('Nakshatra 2', language), kundaliTextLabel('Nakshatra 3', language)],
            colWidths: [14, 40, 20, 34, 34, 34],
            rows: computeNavataraChakra(ad.moonNakshatraNumber).map(r => [r.number, navataraLabel(r.key, language), planetLabel(r.lord, language), r.nakshatras[0], r.nakshatras[1], r.nakshatras[2]]) });
        }
        if (dashaAndNavatara.length) drawKundaliTables(dashaAndNavatara, kundaliTextLabel('Vimsottari & Navatara Chakra', language));

        // Page 2: Birth Nakshatra / Ruling Deity + Best Direction + Astro-Vastu Plant Remedy.
        if (ad.moonNakshatraNumber) {
          const birthNak = getNakshatra(ad.moonNakshatraNumber);
          const nakshatraTables = [
            { title: kundaliTextLabel('Ruling Deity', language), headers: [kundaliTextLabel('Birth Nakshatra', language), kundaliTextLabel('Ruling Deity', language)], colWidths: [90, 90],
              rows: [[birthNak.name, deityLabel(getNakshatraDeity(ad.moonNakshatraNumber), language)]] },
            { title: kundaliTextLabel('Best Direction Table', language), headers: [kundaliTextLabel('Birth Nakshatra', language), kundaliTextLabel('Best Direction', language)], colWidths: [90, 90],
              rows: [[birthNak.name, directionLabel(getNakshatraBestDirection(ad.moonNakshatraNumber), language)]] },
          ];
          if (GRAHA_PLANT_REMEDY[birthNak.lord]) {
            nakshatraTables.push({ title: kundaliTextLabel('Astro-Vastu Plant Remedy', language), headers: [kundaliTextLabel('Planet', language), kundaliTextLabel('Remedy Plant / Tree', language), kundaliTextLabel('Vastu Direction', language)], colWidths: [30, 90, 60],
              rows: Object.entries(GRAHA_PLANT_REMEDY).map(([planet, r]) =>
                [planetLabel(planet, language) + (planet === birthNak.lord ? ' *' : ''), plantLabel(planet, language), directionLabel(r.direction, language)]),
              highlightRow: Object.keys(GRAHA_PLANT_REMEDY).indexOf(birthNak.lord) });
          }
          drawKundaliTables(nakshatraTables, kundaliTextLabel('Nakshatra, Deity & Plant Remedy', language));
        }

        // Hit Theory: Planet to Planet + Planet to House on one page.
        if (ad.hitTheory?.planetToPlanet && ad.hitTheory?.planetToHouse) {
          // Same colours as the on-screen grid's hit codes (HIT_CELL_FG).
          const HIT_BG_RGB = { positive: [21, 128, 61], negative: [185, 28, 28], neutral: [3, 105, 161] };
          const cellText = (v) => {
            if (!v) return '-';
            const text = `${v.displayAngle}°${v.hit ? ' ' + v.hit.code : ''}`;
            return v.hit && HIT_BG_RGB[v.hit.category] ? { text, bg: HIT_BG_RGB[v.hit.category] } : text;
          };
          drawKundaliTables([
            { title: kundaliTextLabel('Hit Theory — Planet to Planet', language), headers: [kundaliTextLabel('Planet', language), ...HIT_PLANETS.map(p => planetLabel(p, language))], colWidths: [24, ...HIT_PLANETS.map(() => 18)],
              rows: HIT_PLANETS.map((source) => [planetLabel(source, language), ...HIT_PLANETS.map((target) => cellText(ad.hitTheory.planetToPlanet[source]?.[target]))]) },
            { title: kundaliTextLabel('Hit Theory — Planet to House', language), headers: [kundaliTextLabel('Planet', language), ...HIT_DISPLAY_HOUSES.map(h => `H${h}`)], colWidths: [24, ...HIT_DISPLAY_HOUSES.map(() => 13)],
              rows: HIT_PLANETS.map((planet) => [planetLabel(planet, language), ...HIT_DISPLAY_HOUSES.map((h) => cellText(ad.hitTheory.planetToHouse[planet]?.[h]))]) },
          ], kundaliTextLabel('Hit Theory', language));
        }

        // House Activity Mapping + Office/Shop/Factory Layout Mapping on one page.
        if (ad.ashtakavarga?.bav) {
          const mapHeaders = [kundaliTextLabel('Planet', language), kundaliTextLabel('Activity', language), directionLabel('East', language), directionLabel('West', language), directionLabel('North', language), directionLabel('South', language)];
          const mapCols = [26, 74, 20, 20, 20, 20];
          // The ASC row's own activity text ("Main Door"/"Main Entry / Plot
          // Facing") isn't a key in ACTIVITY_LABELS — it has its own
          // dedicated translator, ascLabel(mode, lang) — same fix as the
          // live editor's ActivityMappingSection.
          const mapRows = (kind) => buildActivityMapping(ad.ashtakavarga, ad.lagnaRashi, kind)
            .map(r => [planetLabel(r.planet, language) + (r.isApproximated ? ' *' : ''), r.planet === 'ASC' ? ascLabel(kind, language) : r.activities.map(a => activityLabel(a, language)).join(', '), r.east, r.west, r.north, r.south]);
          drawKundaliTables([
            { title: kundaliTextLabel('General House Activity Mapping', language), headers: mapHeaders, colWidths: mapCols, rows: mapRows('house') },
            { title: kundaliTextLabel('Office/Shop/Factory Layout Mapping', language), headers: mapHeaders, colWidths: mapCols, rows: mapRows('office') },
          ], kundaliTextLabel('House & Office Activity Mapping', language));
        }

        const suggestionsText = (ad.suggestionsHtml || '')
          .replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n').replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').trim();
        if (suggestionsText) {
          doc.addPage(); addFrame();
          let sy = projectName ? 34 : 23;
          // This page's own item-identity line — Subtitle tier.
          drawLineSmart(`${person.name || `Person ${pi + 1}`} — ${kundaliTextLabel('General Suggestions & Remedies', language)}`, 14, sy + 4, TYPE_SUBTITLE_SIZE, 'bold', TYPE_BLACK, 'left');
          sy += 15;
          const SUG_CX = 12, SUG_CW = a4W - 24, SUG_PAGE_BOT = a4H - 22;
          suggestionsText.split('\n').filter(l => l.trim()).forEach(line => {
            wrapAny(line.trim(), TYPE_DESCRIPTION_SIZE, 'normal', SUG_CW).forEach(l => {
              if (sy + 8 > SUG_PAGE_BOT) { doc.addPage(); addFrame(); sy = projectName ? 34 : 23; }
              drawLineSmart(l, SUG_CX, sy + 7, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK);
              sy += 8;
            });
          });
          doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(90, 53, 31);
          doc.text('May Vastu bring peace, happiness, prosperity always', a4W / 2, a4H - 15, { align: 'center' });
          doc.setDrawColor(168, 94, 37); doc.setLineWidth(0.25);
          doc.line(a4W / 2 - 20, a4H - 10, a4W / 2 - 3, a4H - 10);
          doc.line(a4W / 2 + 3, a4H - 10, a4W / 2 + 20, a4H - 10);
          doc.setFillColor(168, 94, 37); doc.circle(a4W / 2, a4H - 10, 0.8, 'F');
          doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(124, 66, 28);
          doc.text(`Page ${pi + 1} (suggestions)`, a4W / 2, a4H - 4, { align: 'center' });
        }
      });

      const fname = (projectName || 'Astro_Vastu').replace(/[^a-zA-Z0-9_ -]/g, '').trim().replace(/\s+/g, '_');
      doc.save(`${fname}_Astro_Vastu_D1.pdf`);
    } catch (err) { console.error('Astro PDF error:', err); alert('Error: ' + err.message); }
    finally { setAstroGenerating(false); }
  }, [persons, astroData, projectName]);

  // ── Render helpers ────────────────────────────────────────────────────────
  const btnStyle = (bg = '#00A19D', extra = {}) => ({
    background: bg, color: '#fff', padding: '7px 14px', border: 'none', borderRadius: '6px',
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
    fontSize: '12px', fontWeight: 600, ...extra,
  });

  const ghostBtn = (extra = {}) => ({
    background: 'rgba(255,255,255,0.15)', color: '#fff', padding: '7px 14px',
    border: '1px solid rgba(255,255,255,0.35)', borderRadius: '6px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, ...extra,
  });

  const leave = useLeaveConfirm({
    onSave: () => {
      if (view === 'persons') { persistPersons(persons); handleClose(); }
      else if (view === 'numero' && !kuaMode) saveNumeroData();
      else if (view === 'numero' && kuaMode) { saveKuaData(); handleClose(); }
      else saveAstroData();
    },
    onLeave: handleClose,
  });

  if (!openModal) return null;

  // ── Per-person astro content ──────────────────────────────────────────────
  // Every person's own D1 chart + house editor. Was a single JSX const closing
  // over "the current person" (astroPIdx/curPerson) when only one person was
  // shown at a time via tabs; now every person's page renders simultaneously
  // (stacked, one full page each — no tabs), so this is a function of
  // (person, ad) instead, reading/writing that person's own selected house
  // via getSelHouse/setSelHouseFor rather than one shared selHouse.
  const renderKundaliChartsContent = (person, ad) => {
    const selHouse = getSelHouse(person.id);
    const selHouseData = selHouse ? HOUSES[selHouse - 1] : null;
    const selRashi = selHouse ? getRashi(ad.lagnaRashi, selHouse) : null;
    return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {ad.lagnaRashi && person.dob && person.tob && person.place && ad.birthKey !== birthKeyOf(person) && (
        <div style={{ background: '#fffbeb', border: '1.5px solid #fcd34d', borderRadius: '10px', padding: '10px 14px', fontSize: '12px', color: '#92400e', fontWeight: 600, lineHeight: 1.5 }}>
          {ad.birthKey
            ? 'The date, time or place of birth was changed after this chart was generated, so the chart below may not match. Click "Generate D1 Chart" again to refresh it.'
            : 'This chart was not generated from the birth details currently entered (or was set by hand), so it may not match. Click "Generate D1 Chart" to refresh it.'}
        </div>
      )}
      {/* Moon sign (Rasi) + birth Nakshatra — what other sites (e.g. ProKerala) print as "Rasi".
          Different from the Lagna (Ascendant) Rashi picked below; derived from where the Moon sits in the chart. */}
      {ad.lagnaRashi > 0 && (() => {
        const moonHouse = Object.keys(ad.houses || {}).find((h) => (ad.houses[h] || []).includes('Moon'));
        const moonRashi = moonHouse ? getRashi(ad.lagnaRashi, Number(moonHouse)) : null;
        if (!moonRashi) return null;
        const nak = ad.moonNakshatraNumber ? getNakshatra(ad.moonNakshatraNumber) : null;
        const lagna = getRashi(ad.lagnaRashi, 1);
        return (
          <div style={{ fontSize: '12px', color: '#0f172a', lineHeight: 1.6, background: '#f0fdfa', border: '1px solid #ccfbf1', borderRadius: '8px', padding: '7px 12px' }}>
            <b>Moon Sign (Rasi):</b> {moonRashi.name} ({moonRashi.en})
            {nak && <> &nbsp;·&nbsp; <b>Nakshatra:</b> {nak.name}</>}
            &nbsp;·&nbsp; <b>Lagna (Ascendant):</b> {lagna.name} ({lagna.en})
          </div>
        );
      })()}
      {ad.calcMeta && (
        <div style={{ fontSize: '11px', color: '#64748b', lineHeight: 1.5 }}>
          Calculated for: <b>{ad.calcMeta.dob}</b> · <b>{ad.calcMeta.tob}</b> · {String(ad.calcMeta.place || '').split(',').slice(0, 3).join(',')}
          {' '}({Number(ad.calcMeta.latitude).toFixed(2)}°, {Number(ad.calcMeta.longitude).toFixed(2)}° · UTC{Number(ad.calcMeta.timezone) >= 0 ? '+' : ''}{ad.calcMeta.timezone})
        </div>
      )}
      {/* Lagna Rashi manual picker — moved here from the top toolbar since
          it's what the chart + house editor key off, so it only makes
          sense right alongside the chart itself, not floating above every
          report tab (Vimsottari, Navatara, etc. don't need it). */}
      <div style={{ background: '#fff', borderRadius: '10px', border: '1.5px solid #e2e8f0', padding: isMobile ? '10px 12px' : '12px 16px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Lagna Rashi</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {RASHIS.map(r => (
            <button key={r.num} onClick={() => setLagnaRashi(person.id, ad.lagnaRashi === r.num ? 0 : r.num)}
              style={{ padding: '3px 8px', borderRadius: '10px', border: ad.lagnaRashi === r.num ? '2px solid #00A19D' : '1.5px solid #e2e8f0', background: ad.lagnaRashi === r.num ? '#e6f7f7' : '#f8fafc', color: ad.lagnaRashi === r.num ? '#00A19D' : '#475569', fontSize: '10px', fontWeight: ad.lagnaRashi === r.num ? 700 : 500, cursor: 'pointer', transition: 'all 0.15s', display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.25 }}>
              {r.name}
              <span style={{ opacity: 0.6, fontSize: '8px' }}>{rashiLabel(r.en, language)}</span>
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '16px', alignItems: 'stretch', flexWrap: 'wrap' }}>
      <div style={{ flex: '2 1 520px' }}>
        <D1Chart
          lagnaRashi={ad.lagnaRashi}
          houses={ad.houses || {}}
          ascendantNormDegree={ad.ascendantNormDegree}
          selectedHouse={selHouse}
          onSelectHouse={(h) => setSelHouseFor(person.id, h)}
          language={language}
          chartStyles={chartStyles}
          onToggleStyle={toggleChartStyle}
        />
      </div>

      {/* House/planet editor — only takes up space once a house is actually
          clicked; no more empty placeholder box sitting there beforehand. */}
      {selHouse && (
      <div style={{ flex: '1 1 260px', background: '#fff', borderRadius: '10px', border: '1.5px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* House header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: '#00A19D', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '15px', flexShrink: 0 }}>{selHouse}</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a' }}>{selHouseData?.name} Bhav</div>
              <div style={{ fontSize: '10px', color: '#64748b', lineHeight: 1.4 }}>{selHouseData?.desc}</div>
            </div>
          </div>

          {/* Rashi */}
          {selRashi ? (
            <div style={{ background: '#f0fdfa', border: '1.5px solid #e6f7f7', borderRadius: '8px', padding: '8px 10px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Rashi</div>
              <div style={{ fontWeight: 700, fontSize: '13px', color: '#00A19D' }}>{selRashi.name} <span style={{ color: '#94a3b8', fontWeight: 400 }}>({rashiLabel(selRashi.en, language)})</span></div>
            </div>
          ) : (
            <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '8px', padding: '8px 10px', fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
              Set Lagna Rashi to see house rashi
            </div>
          )}

          {/* Planets */}
          <div>
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>Grahas (Planets)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {GRAHAS.map(g => {
                const isSelected = (ad.houses?.[selHouse] || []).includes(g);
                return (
                  <label key={g} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', borderRadius: '7px', background: isSelected ? '#fef9c3' : 'transparent', border: isSelected ? '1.5px solid #fbbf24' : '1.5px solid transparent', cursor: 'pointer', fontSize: '12px', fontWeight: isSelected ? 700 : 400, color: isSelected ? '#713f12' : '#374151', transition: 'all 0.12s', userSelect: 'none' }}>
                    <input type="checkbox" checked={isSelected}
                      onChange={() => togglePlanet(person.id, selHouse, g)}
                      style={{ accentColor: '#00A19D', width: '14px', height: '14px' }} />
                    {g}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Clear house */}
          {(ad.houses?.[selHouse] || []).length > 0 && (
            <button onClick={() => setAstroData(prev => ({ ...prev, [person.id]: { ...(prev[person.id] || newAstroPersonData()), houses: { ...(prev[person.id]?.houses || {}), [selHouse]: [] } } }))}
              style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: 600, marginTop: '4px' }}>
              Clear Planets from House {selHouse}
            </button>
          )}
        </div>
      </div>
      )}
      </div>
    </div>
    );
  };

  // "Generate Chart" — now lives at the top of the right-side Kundali
  // Reports nav instead of its own toolbar card above the tabs, since it's
  // the one action needed no matter which report tab is currently open.
  // Every OTHER person's button is disabled (not just this one's) while any
  // single generate (or Generate All) is in flight — see astroApiLoadingId's
  // comment for why that's intentional rather than a bug.
  const renderGenerateButton = (person, ad) => {
    const isThisLoading = astroApiLoadingId === person.id;
    const disabled = astroApiLoading || astroGenerateAllLoading;
    return (
    <div style={{ padding: '12px 14px', borderBottom: '1px solid #e2e8f0' }}>
      <div style={{ position: 'relative', display: 'inline-flex', width: '100%' }}>
        {/* Pulses for ~3s (stops on its own, finite iteration count) and
            shows an animated "click here" pointer icon tapping the button
            once when the view opens — to draw the eye to it before a chart
            exists yet. Once calculated, neither shows again. */}
        <button onClick={() => calcAstroFromApi(person)} disabled={disabled}
          className={!ad.lagnaRashi && !disabled ? 'astro-gen-pulse' : ''}
          style={{ width: '100%', justifyContent: 'center', background: disabled ? 'rgba(0,0,0,0.25)' : '#00A19D', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 14px', fontSize: '12px', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '7px' }}>
          {isThisLoading ? <span className="nv-spinner" /> : <Sparkles size={14} />}
          {isThisLoading ? 'Calculating…' : 'Generate Chart'}
        </button>
        {!ad.lagnaRashi && !disabled && (
          <MousePointerClick size={20} className="astro-gen-pointer"
            style={{ position: 'absolute', right: -6, bottom: -10, color: '#0f172a', pointerEvents: 'none' }} />
        )}
      </div>
    </div>
    );
  };

  return (
    // Full-bleed edge-to-edge, same as every other report modal (Feng Shui,
    // Vastu Devta Guide, Navgrah Shanti, Zone Analysis) — this one used to be
    // a centered floating card over a dark overlay instead, which read as a
    // smaller/different "popup" rather than a full report screen.
    /* z-index bumped above the mobile bottom toolbar (10100)/sheets — this
       used to render behind them on phone, looking like it never opened. */
    <div style={{ position: 'fixed', inset: 0, zIndex: 10500, display: 'flex', flexDirection: 'column', background: '#f1f5f9' }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '10px 12px' : '10px 20px', background: '#00A19D', borderBottom: '1px solid rgba(255,255,255,0.2)', minHeight: '56px', gap: '12px', flexShrink: 0 }}>
        {/* Left: icon + title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '10px', minWidth: 0 }}>
          <div style={{ width: 34, height: 34, borderRadius: 8, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {view === 'astro' ? <Star size={18} color="#fff" /> : <Hash size={18} color="#fff" />}
          </div>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ color: '#fff', margin: 0, fontSize: isMobile ? '13.5px' : '15px', fontWeight: 700, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {view === 'persons' ? 'Astro Vastu & Numero Vastu' : view === 'numero' ? (kuaMode ? 'Kua Number Analysis' : 'Numero Vastu') : 'Astro Report'}
            </h2>
            {/* Subtitle is decorative context, not essential — dropped on
                mobile to keep the header from growing tall next to the
                icon-only action buttons. */}
            {!isMobile && (
              <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '11px' }}>
                {view === 'persons' ? 'Enter person details to continue' : view === 'numero' ? (kuaMode ? 'Edit analysis per person' : 'Lo Shu Grid Report') : 'Birth Chart, Dasha & Vastu Reports'}
              </div>
            )}
          </div>
        </div>

        {/* Right: action buttons — full text+icon on desktop; icon-only
            (with a title tooltip) on mobile so the row never wraps into a
            multi-line header, matching FengShui.jsx's approach. Every
            handler/condition below is identical to desktop, only the
            button's visible content changes. */}
        <div style={{ display: 'flex', gap: isMobile ? '6px' : '8px', flexWrap: 'wrap', flexShrink: 0 }}>
          {view === 'persons' && (
            <>
              <button onClick={handlePersonAdd} title="Add Person" style={isMobile ? ghostBtn({ padding: '7px 9px' }) : ghostBtn()}><Plus size={14} />{!isMobile && ' Add Person'}</button>
              <button onClick={() => { persistPersons(persons); alert('Saved!'); }} title="Save" style={isMobile ? btnStyle('#28a745', { padding: '7px 9px' }) : btnStyle('#28a745')}><Save size={14} />{!isMobile && ' Save'}</button>
              <button onClick={leave.requestClose} title="Close" style={{ background: '#dc3545', color: '#fff', padding: '7px 12px', border: 'none', borderRadius: '6px', cursor: 'pointer' }}><X size={16} /></button>
            </>
          )}

          {view === 'numero' && !kuaMode && (
            <>
              <button onClick={handlePersonAdd} title="Add Person" style={isMobile ? ghostBtn({ padding: '7px 9px' }) : ghostBtn()}><Plus size={14} />{!isMobile && ' Add Person'}</button>
              <button onClick={enterKuaMode} title="Kua Number" style={isMobile ? ghostBtn({ padding: '7px 9px' }) : ghostBtn()}><Hash size={14} />{!isMobile && ' Kua Number'}</button>
              <button onClick={downloadNumeroPDF} disabled={isGenerating} title={isGenerating ? 'Generating…' : 'Download PDF'} style={btnStyle(isGenerating ? 'rgba(0,0,0,0.3)' : '#00857E', { opacity: isGenerating ? 0.65 : 1, cursor: isGenerating ? 'not-allowed' : 'pointer', ...(isMobile ? { padding: '7px 9px' } : {}) })}>
                {isGenerating ? <span className="nv-spinner" /> : <Download size={14} />}{!isMobile && (isGenerating ? ' Generating...' : ' Download PDF')}
              </button>
              <button onClick={saveNumeroData} title="Save Report" style={isMobile ? btnStyle('#28a745', { padding: '7px 9px' }) : btnStyle('#28a745')}><Save size={14} />{!isMobile && ' Save Report'}</button>
              <button onClick={leave.requestClose} title="Close" style={{ background: '#dc3545', color: '#fff', padding: '7px 12px', border: 'none', borderRadius: '6px', cursor: 'pointer' }}><X size={16} /></button>
            </>
          )}

          {view === 'numero' && kuaMode && (
            <>
              <button onClick={() => setKuaMode(false)} title="Back to Persons" style={isMobile ? ghostBtn({ padding: '7px 9px' }) : ghostBtn()}><ChevronLeft size={14} />{!isMobile && ' Back to Persons'}</button>
              <button onClick={downloadKuaPDF} disabled={kuaGenerating} title={kuaGenerating ? 'Generating…' : 'Download Kua PDF'} style={btnStyle(kuaGenerating ? 'rgba(0,0,0,0.3)' : '#00857E', { opacity: kuaGenerating ? 0.65 : 1, cursor: kuaGenerating ? 'not-allowed' : 'pointer', ...(isMobile ? { padding: '7px 9px' } : {}) })}>
                {kuaGenerating ? <span className="nv-spinner" /> : <Download size={14} />}{!isMobile && (kuaGenerating ? ' Generating...' : ' Download Kua PDF')}
              </button>
              <button onClick={saveKuaData} title="Save Kua Data" style={isMobile ? btnStyle('#28a745', { padding: '7px 9px' }) : btnStyle('#28a745')}><Save size={14} />{!isMobile && ' Save Kua Data'}</button>
              <button onClick={leave.requestClose} title="Close" style={{ background: '#dc3545', color: '#fff', padding: '7px 12px', border: 'none', borderRadius: '6px', cursor: 'pointer' }}><X size={16} /></button>
            </>
          )}

          {view === 'astro' && (
            <>
              <button onClick={downloadAstroPDF} disabled={astroGenerating} title={astroGenerating ? 'Generating…' : 'Download PDF'} style={btnStyle(astroGenerating ? 'rgba(0,0,0,0.3)' : '#00857E', { opacity: astroGenerating ? 0.65 : 1, cursor: astroGenerating ? 'not-allowed' : 'pointer', ...(isMobile ? { padding: '7px 9px' } : {}) })}>
                {astroGenerating ? <span className="nv-spinner" /> : <Download size={14} />}{!isMobile && (astroGenerating ? ' Generating...' : ' Download PDF')}
              </button>
              <button onClick={saveAstroData} title="Save Astro" style={isMobile ? btnStyle('#28a745', { padding: '7px 9px' }) : btnStyle('#28a745')}><Save size={14} />{!isMobile && ' Save Astro'}</button>
              <button onClick={leave.requestClose} title="Close" style={{ background: '#dc3545', color: '#fff', padding: '7px 12px', border: 'none', borderRadius: '6px', cursor: 'pointer' }}><X size={16} /></button>
            </>
          )}
        </div>
      </div>

      {leave.dialog}

      {/* Auto-prompt: bulk-generate every person's Astro report in one go —
          replaces the old always-visible "Generate All" header button.
          Shown right when the Astro view opens if any person still needs
          generating (see the effect that computes showGenerateAllPrompt). */}
      {showGenerateAllPrompt && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10050, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '14px' : '20px' }}>
          <div style={{ background: '#fff', borderRadius: '14px', maxWidth: '420px', width: '100%', padding: isMobile ? '18px' : '24px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', boxSizing: 'border-box' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: isMobile ? '14.5px' : '16px', fontWeight: 800, color: '#0f172a' }}>Generate Astro Report for everyone?</h3>
            <p style={{ margin: '0 0 20px', fontSize: '12.5px', color: '#64748b', lineHeight: 1.6 }}>
              One or more people here haven't had their Kundali chart and reports generated yet. Generate them all now,
              instead of doing it one person at a time.
            </p>
            {astroApiError && (
              <div style={{ fontSize: '11px', color: '#dc2626', fontWeight: 600, marginBottom: '14px', lineHeight: 1.4 }}>{astroApiError}</div>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexDirection: isMobile ? 'column-reverse' : 'row' }}>
              <button onClick={() => setGenerateAllPromptDismissed(true)} disabled={astroGenerateAllLoading}
                style={{ background: '#f1f5f9', color: '#334155', border: 'none', borderRadius: '8px', padding: '9px 16px', fontSize: '12.5px', fontWeight: 600, cursor: astroGenerateAllLoading ? 'not-allowed' : 'pointer', width: isMobile ? '100%' : 'auto', boxSizing: 'border-box' }}>
                Skip for now
              </button>
              <button onClick={async () => { await calcAllAstroFromApi(); setGenerateAllPromptDismissed(true); }} disabled={astroGenerateAllLoading}
                style={{ background: astroGenerateAllLoading ? 'rgba(0,0,0,0.25)' : '#00A19D', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 16px', fontSize: '12.5px', fontWeight: 700, cursor: astroGenerateAllLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', width: isMobile ? '100%' : 'auto', boxSizing: 'border-box' }}>
                {astroGenerateAllLoading
                  ? <><span className="nv-spinner" /> Generating {astroGenerateAllProgress?.done ?? 0}/{astroGenerateAllProgress?.total ?? 0}…</>
                  : <><Sparkles size={14} /> Generate All</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Body ───────────────────────────────────────────────────────────── */}

      {/* ── VIEW: Persons ────────────────────────────────────────────────── */}
      {view === 'persons' && (
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, padding: isMobile ? '14px' : '24px', maxWidth: '860px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
            {persons.map((person, idx) => (
              <PersonFormCard key={person.id} language={language} person={person} index={idx}
                onChange={handlePersonChange} onRemove={handlePersonRemove} canRemove={persons.length > 1} />
            ))}
            <button onClick={handlePersonAdd} style={{ width: '100%', padding: '12px', background: '#fff', border: '2px dashed #00A19D', borderRadius: '10px', color: '#00A19D', cursor: 'pointer', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '28px' }}>
              <Plus size={16} /> Add Another Person
            </button>
          </div>

          {/* ── Choice section ── */}
          <div style={{ background: '#fff', borderTop: '1.5px solid #e2e8f0', padding: isMobile ? '20px 14px' : '28px 24px', flexShrink: 0 }}>
            <p style={{ textAlign: 'center', margin: '0 0 18px', fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
              Choose the report type for the entered person(s)
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', maxWidth: '620px', margin: '0 auto' }}>
              {/* Numero Vastu */}
              <button onClick={goToNumero} style={{ flex: '1 1 240px', minWidth: '220px', background: '#f0fdfa', border: '2px solid #00A19D', borderRadius: '14px', padding: '22px 20px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#ccfbf1'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,161,157,0.18)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#f0fdfa'; e.currentTarget.style.boxShadow = 'none'; }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: '#00A19D', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                  <Hash size={22} color="#fff" />
                </div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>Numero Vastu</div>
                <div style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.5 }}>Lo Shu Grid · Kua Number · Strength Analysis</div>
              </button>

              {/* Astro Vastu */}
              <button onClick={goToAstro} style={{ flex: '1 1 240px', minWidth: '220px', background: '#f0fdfa', border: '2px solid #00A19D', borderRadius: '14px', padding: '22px 20px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#e6f7f7'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(124,58,237,0.18)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#f0fdfa'; e.currentTarget.style.boxShadow = 'none'; }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: '#00A19D', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                  <Star size={22} color="#fff" />
                </div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>Astro Report</div>
                <div style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.5 }}>Birth Chart · Dasha · Vastu Reports</div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── VIEW: Numero ─────────────────────────────────────────────────── */}
      {view === 'numero' && !kuaMode && (
        <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '14px' : '24px', maxWidth: '1100px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
          {persons.map((person, idx) => (
            <PersonFormCard key={person.id} language={language} person={person} index={idx}
              onChange={handlePersonChange} onRemove={handlePersonRemove}
              canRemove={persons.length > 1} showGrid />
          ))}
          <button onClick={handlePersonAdd} style={{ width: '100%', padding: '14px', background: '#fff', border: '2px dashed #00A19D', borderRadius: '10px', color: '#00A19D', cursor: 'pointer', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <Plus size={16} /> Add Another Person
          </button>
        </div>
      )}

      {/* ── VIEW: Kua ────────────────────────────────────────────────────── */}
      {view === 'numero' && kuaMode && (
        <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '14px' : '24px', background: '#f1f5f9' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ marginBottom: '18px' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>Kua Number Analysis</h3>
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>Auto-calculated from each person's DOB & gender. Edit freely.</p>
            </div>

            {/* Side-by-side comparison table — Profile, Numerology, Favourable
                & Challenging Direction, one column per person. Already has
                its own overflow:auto + minWidth wrapper (see
                KuaComparisonTable), so it needs no mobile-specific change —
                it scrolls horizontally within itself on a phone. */}
            {persons.filter(p => p.dob).length > 0 && <KuaComparisonTable persons={persons.filter(p => p.dob)} language={language} />}

            {/* minmax(420px,…) never fits a ~360-400px phone screen — one
                column on mobile instead. */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(420px, 1fr))', gap: isMobile ? '12px' : '16px' }}>
              {kuaItems.map(item => (
                <KuaCard key={item.id} item={item} language={language} onHtmlChange={handleKuaHtmlChange} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── VIEW: Astro ──────────────────────────────────────────────────── */}
      {view === 'astro' && (
        // The scroll container lives right here, one single level below the
        // already-reliable outer fixed-viewport chain — two earlier attempts
        // nested overflow:auto one or two levels deeper (inside the row,
        // then inside a column within the row) and neither reliably picked
        // up a bounded height across all cases. This level is the simplest
        // one that's provably height-bound (it's what made the D1 chart
        // itself render at full, uncut size), so it's the safest place to
        // own scrolling for everything below it.
        <div onScroll={handleAstroScroll} style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '14px' : '24px', paddingTop: persons.length > 1 ? 0 : (isMobile ? '14px' : '24px'), maxWidth: '1100px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
          {/* Member name bar — jump straight to one member without scrolling past everyone else.
              The stacked full pages below are unchanged; this only adds quick navigation. */}
          {persons.length > 1 && (
            <div style={{ position: 'sticky', top: 0, zIndex: 20, background: '#f1f5f9', padding: isMobile ? '10px 0' : '14px 0 12px', marginBottom: '8px' }}>
              <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '2px' }}>
                {persons.map((p, i) => {
                  const active = (activeAstroPersonId || persons[0]?.id) === p.id;
                  return (
                    <button key={p.id} type='button' onClick={() => jumpToAstroPerson(p.id)} title={p.name || `Person ${i + 1}`}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, maxWidth: '220px', padding: '6px 12px 6px 6px', borderRadius: '10px', cursor: 'pointer', border: active ? '1.5px solid #00A19D' : '1.5px solid #e2e8f0', background: active ? '#00A19D' : '#fff', color: active ? '#fff' : '#0f172a', fontSize: '12.5px', fontWeight: 700, boxShadow: active ? '0 2px 8px rgba(0,161,157,0.3)' : 'none', transition: 'all 0.15s' }}>
                      <span style={{ width: 22, height: 22, borderRadius: 6, background: active ? 'rgba(255,255,255,0.25)' : '#00A19D', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', flexShrink: 0 }}>{i + 1}</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name || `Person ${i + 1}`}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {astroApiError && (
            <div style={{ marginBottom: '16px', padding: '9px 12px', background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '11.5px', fontWeight: 600, lineHeight: 1.4 }}>
              {astroApiError}
            </div>
          )}

          {/* One full page per person, stacked top to bottom — no tabs to
              switch between people. Same card chrome as PersonFormCard above
              (Numero Vastu view) — white rounded bordered card, teal number
              badge + name header — just with the Astro report inside instead
              of the birth-detail fields. Each person keeps their own
              chart/house-editor state (see getSelHouse), so editing one
              person's chart never touches another's. */}
          {persons.map((person, idx) => {
            const ad = getAD(person.id);
            return (
              <div key={person.id} id={`astro-person-${person.id}`} style={{ background: '#fff', borderRadius: '12px', border: '1.5px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden', marginBottom: '16px', scrollMarginTop: persons.length > 1 ? '76px' : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: isMobile ? '10px 12px' : '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: '#00A19D', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '14px', flexShrink: 0 }}>{idx + 1}</div>
                  <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{person.name || `Person ${idx + 1}`}</div>
                </div>

                {/* Single column — chart, its house-editor, then Analysis
                    Notes stacked in reading order (was chart+notes on the
                    left and the house-editor as a separate side panel on the
                    right; merged into one column per request). */}
                <div style={{ width: '100%', padding: isMobile ? '14px' : '18px 20px', display: 'flex', flexDirection: 'column', gap: '16px', boxSizing: 'border-box' }}>
                    {/* Astro Report — every Kundali report section (Vimsottari,
                        Navatara Chakra, etc.) one after another, no side nav to
                        switch between them any more. */}
                    <KundaliReportsPanel astroData={ad}
                      personId={person.id} onSuggestionsChange={handleSuggestionsChange} chartsContent={renderKundaliChartsContent(person, ad)}
                      generateButton={renderGenerateButton(person, ad)} language={language} />

                    {/* Rich text analysis — last, below the chart and its house editor */}
                    <div style={{ background: '#fff', borderRadius: '10px', border: '1.5px solid #e2e8f0', overflow: 'hidden' }}>
                      <div style={{ padding: '10px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{kundaliTextLabel('Analysis Notes', language)}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                          {ad.lagnaRashi > 0 && !isDefaultAstroHtml(ad.lagnaRashi, ad.houses || {}, getRashi, ad.richHtml) && (
                            <span
                              title="Edited — won't auto-translate when you switch language. Edit it again in each language you need."
                              style={{ fontSize: '10px', color: '#b45309', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, padding: '2px 6px', fontWeight: 700 }}
                            >
                              Edited
                            </span>
                          )}
                          {ad.lagnaRashi > 0 && (
                            <button
                              onClick={() => handleAstroHtmlChange(person.id, buildAstroAnalysisHtml(ad.lagnaRashi, ad.houses || {}, getRashi, language))}
                              title="Rebuild these notes from the current chart — replaces existing text"
                              style={{ background: '#f0fdfa', color: '#00857E', border: '1px solid #ccfbf1', borderRadius: '6px', padding: '4px 9px', fontSize: '10.5px', fontWeight: 600, cursor: 'pointer' }}>
                              Generate from Chart
                            </button>
                          )}
                        </div>
                      </div>
                      <RichEditor itemId={person.id} value={ad.richHtml} onChange={handleAstroHtmlChange} />
                    </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes nvSpin { from { transform:rotate(0deg) } to { transform:rotate(360deg) } }
        .nv-spinner { width:12px; height:12px; border:2px solid rgba(255,255,255,0.6); border-top-color:#fff; border-radius:50%; animation:nvSpin 0.8s linear infinite; display:inline-block; }
        @keyframes astroGenPulse { 0% { box-shadow:0 0 0 0 rgba(0,161,157,0.55); } 70% { box-shadow:0 0 0 10px rgba(0,161,157,0); } 100% { box-shadow:0 0 0 0 rgba(0,161,157,0); } }
        .astro-gen-pulse { animation: astroGenPulse 1.1s ease-out 3; }
        @keyframes astroGenPointerTap {
          0%   { transform: translate(6px,6px) scale(1);    opacity: 0; }
          15%  { transform: translate(6px,6px) scale(1);    opacity: 1; }
          30%  { transform: translate(0,0)     scale(0.85); opacity: 1; }
          45%  { transform: translate(6px,6px) scale(1);    opacity: 1; }
          60%  { transform: translate(0,0)     scale(0.85); opacity: 1; }
          80%  { transform: translate(6px,6px) scale(1);    opacity: 1; }
          100% { transform: translate(6px,6px) scale(1);    opacity: 0; }
        }
        .astro-gen-pointer { animation: astroGenPointerTap 3.2s ease-in-out 1 both; }
        .space-editor h2:first-child { font-size:1rem; font-weight:800; color:#0f172a; margin:0 0 10px; border-bottom:2px solid #000; padding-bottom:5px; text-align:left !important; }
        .space-editor h2 { font-size:0.82rem; font-weight:700; color:#1e293b; margin:10px 0 2px; text-align:left !important; }
        .space-editor p { margin:2px 0 4px; color:#374151; font-size:0.82rem; line-height:1.55; text-align:left !important; }
        .space-editor { text-align:left !important; }
        .space-editor[data-placeholder]:empty::before { content:attr(data-placeholder); color:#cbd5e1; pointer-events:none; }
      `}</style>
    </div>
  );
}
